import React, { useState, useEffect, useRef } from "react";
import {
  useMediaQuery,
  useTheme,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  RadioGroup,
  FormControl,
  FormLabel,
  Radio,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Stack,
  Collapse,
  Alert,
  Snackbar,
  Chip,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import {
  AddRounded,
  DeleteRounded,
  DragIndicatorRounded,
  ExpandMoreRounded,
  SaveRounded,
  PrintRounded,
  Phone,
  LocationOn,
  Person,
  Receipt,
  AttachMoney,
  Diamond,
  AccountBalance,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  BorderColor,
  BorderStyle,
  Palette,
  ViewCompact,
  ViewModule,
  ViewList,
  Layers,
  SaveAlt,
  RestartAlt,
  Preview,
  CloudUpload,
  Download,
  DeleteForever,
  Star,
  StarBorder,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "axios";
import { useLocation } from "react-router-dom";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import { QRCodeSVG } from "qrcode.react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { buildTicketPayload, encodeTicketData } from "./ticketQrCodec";
import PawnNavButtons from "./pawnNavButtons";
import CanvasSheet, { COPY_LABEL_CLASS } from "./ticketCanvas/CanvasSheet";
import TicketCanvasEditor from "./ticketCanvas/TicketCanvasEditor";
import {
  CanvasLayers,
  CanvasPalette,
  CanvasProperties,
} from "./ticketCanvas/CanvasSidePanel";
import useCanvasHistory from "./ticketCanvas/useCanvasHistory";
import {
  BLOCK_DEFS,
  CANVAS_VERSION,
  createElement,
  normalizeElements,
  paperDims,
  rescaleElements,
  round1,
  seedCanvasLayout,
} from "./ticketCanvas/canvasModel";
import DashboardCustomizeRounded from "@mui/icons-material/DashboardCustomizeRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import LayersRounded from "@mui/icons-material/LayersRounded";
import AddBoxRounded from "@mui/icons-material/AddBoxRounded";
import StorefrontRounded from "@mui/icons-material/StorefrontRounded";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// Helper: number to words (Indian Rupees)
const numberToWords = (num) => {
  if (!num || num === 0) return "Zero Rupees Only";
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const convert = (n) => {
    if (n === 0) return "";
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100)
      return (
        tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "")
      );
    if (n < 1000)
      return (
        ones[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 !== 0 ? " " + convert(n % 100) : "")
      );
    if (n < 100000)
      return (
        convert(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 !== 0 ? " " + convert(n % 1000) : "")
      );
    if (n < 10000000)
      return (
        convert(Math.floor(n / 100000)) +
        " Lakh" +
        (n % 100000 !== 0 ? " " + convert(n % 100000) : "")
      );
    return (
      convert(Math.floor(n / 10000000)) +
      " Crore" +
      (n % 10000000 !== 0 ? " " + convert(n % 10000000) : "")
    );
  };
  return convert(num) + " Rupees Only";
};

// ─── Placeholder images used in designer/edit mode ───────────────────────────
// When the generator is opened without real transaction data (template design),
// these dummy pictures (an avatar for the customer, a star for the ornaments)
// let the user see and lay out the image slots.
const DUMMY_CUSTOMER_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect width="120" height="120" rx="8" fill="#E2E8F0"/><circle cx="60" cy="46" r="22" fill="#94A3B8"/><path d="M22 108c0-21 17-36 38-36s38 15 38 36z" fill="#94A3B8"/></svg>`,
  );
const DUMMY_ORNAMENT_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect width="120" height="120" rx="8" fill="#FEF3C7"/><path d="M60 18l12.9 26.1 28.8 4.2-20.8 20.3 4.9 28.7L60 83.9 34.2 97.5l4.9-28.7L18.3 48.5l28.8-4.2z" fill="#F59E0B"/></svg>`,
  );

// Sortable section wrapper.
//
// `children` is a render function that receives the drag-handle props
// ({ attributes, listeners }). Those props MUST be attached to a dedicated
// handle (the grip icon) only — NOT the whole row. Spreading the drag
// listeners over the entire row made dnd-kit's PointerSensor swallow clicks on
// the visibility Switch as drag-starts, so toggling never fired.
const SortableSection = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    // No bottom margin: the list's <Stack spacing> already sets the gap, and the
    // extra 16px here tripled the space between rows.
    <Box ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </Box>
  );
};

// ─── Blue/White Design Tokens (shared with the Pawn module) ─────────────────
// Re-declared locally, the way every other pawn screen does it. The rule the
// module follows: white surfaces, slate borders, ONE blue accent, flat cards
// (never an elevated Paper), compact type.
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_PALE = "#EFF6FF";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BORDER_MED = "#CBD5E1";
const SURFACE = "#FFFFFF";
const BG_ALT = "#F1F5F9";
const HEAD_BG = "#F8FAFC";
// The grey the paper sits on. A shade deeper than the page so the sheet reads as
// a document on a workbench rather than a panel on a panel.
const CANVAS_BG = "#E8EDF3";

const primaryBtnSx = {
  bgcolor: BLUE,
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.78rem",
  boxShadow: "none",
  "&:hover": { bgcolor: BLUE_DARK, boxShadow: "none" },
};
const outlinedBtnSx = {
  color: BLUE,
  borderColor: BORDER_MED,
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.78rem",
  "&:hover": { borderColor: BLUE, bgcolor: BLUE_PALE },
};
// Uppercase tracked micro-label sitting above a control group.
const microLabelSx = {
  fontSize: "0.6rem",
  fontWeight: 700,
  color: INK_MUTED,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  display: "block",
  mb: 0.75,
};
// The blue rounded icon tile every pawn page header opens with.
const iconTileSx = {
  width: { xs: 34, sm: 38 },
  height: { xs: 34, sm: 38 },
  flexShrink: 0,
  borderRadius: 2,
  bgcolor: BLUE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
};
// Compact input: 8px corners, slate border, smaller type. Shared by every Select
// in the panel so the control rows stop dominating the card.
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.82rem",
    bgcolor: SURFACE,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: BORDER_MED },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: 1 },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: INK_MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE },
};
// A colour swatch reads better as a short wide block than as a text input. Its
// label goes ABOVE it (see microLabelSx) rather than floating in the outline —
// a notched label crosses a swatch this short.
const colorFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    p: 0.5,
    bgcolor: SURFACE,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: BORDER_MED },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: 1 },
  },
  "& input[type=color]": {
    height: 24,
    padding: 0,
    cursor: "pointer",
    borderRadius: "4px",
  },
};
// Slider row: the label carries the value, the track is thin, and the vertical
// padding is trimmed — a default MUI slider reserves ~20px of dead space.
const sliderLabelSx = {
  fontSize: "0.7rem",
  fontWeight: 600,
  color: INK_SOFT,
  display: "block",
};
const sliderSx = {
  color: BLUE,
  py: 1,
  mt: 0.25,
  mb: -0.5,
  "& .MuiSlider-rail": { opacity: 1, bgcolor: BORDER },
  "& .MuiSlider-thumb": {
    width: 13,
    height: 13,
    "&:hover, &.Mui-focusVisible": {
      boxShadow: "0 0 0 6px rgba(37,99,235,0.16)",
    },
  },
};
// Switch + label pair inside a settings card.
const switchLabelSx = {
  m: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: INK_SOFT,
  },
};
// Selectable pill (ticket type, quick layout). Flat both ways — the old lift-on-
// hover shadow is what made the panel read as dated.
const pillSx = (selected) => ({
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "0.72rem",
  height: 26,
  borderRadius: "13px",
  transition: "background-color .15s ease, border-color .15s ease",
  ...(selected
    ? {
        bgcolor: BLUE,
        color: "#fff",
        border: `1px solid ${BLUE}`,
        "&:hover": { bgcolor: BLUE_DARK },
      }
    : {
        bgcolor: SURFACE,
        color: INK_SOFT,
        border: `1px solid ${BORDER}`,
        "&:hover": { bgcolor: BLUE_PALE, borderColor: BLUE },
      }),
});

// Rich-text editor config for the Terms & Conditions section. A full toolbar
// so a store can paste/format terms in ANY language (e.g. Odia) and have it
// print exactly as typed.
const TERMS_QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["clean"],
  ],
};
const TERMS_QUILL_FORMATS = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "indent",
  "align",
];

// Default terms shown until a store customises them.
const DEFAULT_TERMS_HTML =
  "<ol>" +
  "<li>The pawner declares that the articles are their own and free from any encumbrance.</li>" +
  "<li>Interest will be charged at the agreed rate until full repayment.</li>" +
  "<li>The pledged articles will be returned only upon full payment of principal and interest.</li>" +
  "<li>In case of default, the pawnee reserves the right to auction the pledged articles.</li>" +
  "</ol>";

// Layout Templates
const LAYOUT_TEMPLATES = {
  classic: {
    name: "Classic",
    sections: ["store", "ticketNo", "customer", "loan", "items", "signatures"],
    style: "classic",
  },
  modern: {
    name: "Modern",
    sections: ["store", "customer", "loan", "payment", "items", "signatures"],
    style: "modern",
  },
  minimal: {
    name: "Minimal",
    sections: ["ticketNo", "customer", "loan", "items"],
    style: "minimal",
  },
  detailed: {
    name: "Detailed",
    sections: [
      "store",
      "ticketNo",
      "customer",
      "loan",
      "payment",
      "items",
      "signatures",
      "terms",
    ],
    style: "detailed",
  },
};

// Designer Mode Banner Component
const DesignerModeBanner = () => (
  <Box
    sx={{
      bgcolor: "#FFF3E0",
      borderBottom: `2px solid #FF9800`,
      p: 0.75,
      textAlign: "center",
      mb: 1.5,
      borderRadius: 1,
    }}
  >
    <Typography variant="caption" sx={{ color: "#E65100" }}>
      🎨 Design Mode - You're editing a template. Changes won't affect real
      data.
    </Typography>
  </Box>
);

// A titled, bordered card used to group the controls in the left panel so each
// settings group (Paper, Typography, Colors, Extras, Sections…) reads as its
// own distinct block instead of a flat run of headings.
//
// The whole header strip is a toggle, so a group can be folded away once it has
// been configured. That matters most on a phone or tablet, where the panel stacks
// ABOVE the ticket and every expanded card pushes the preview further down the
// page — hence `defaultOpen`, which the panel sets from the breakpoint. `summary`
// keeps the group's key values legible while collapsed, so a card never has to be
// reopened just to check what it is set to.
const SettingsCard = ({
  title,
  icon,
  action,
  summary,
  children,
  sx,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  // Re-sync when the viewport crosses the breakpoint that decides the default
  // (e.g. a tablet rotated into landscape).
  useEffect(() => setOpen(defaultOpen), [defaultOpen]);
  const toggle = () => setOpen((o) => !o);
  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: SURFACE,
        mb: 1.25,
        ...sx,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: open ? HEAD_BG : SURFACE,
          // Only draw the rule while the body below it is showing, so a collapsed
          // card reads as a single bar instead of an empty box.
          borderBottom: open ? `1px solid ${BORDER}` : "none",
          cursor: "pointer",
          userSelect: "none",
          transition: "background-color .15s ease",
          "&:hover": { bgcolor: BLUE_PALE },
        }}
      >
        {icon &&
          React.cloneElement(icon, {
            sx: {
              fontSize: 17,
              color: BLUE,
              ...(icon.props.sx || {}),
            },
          })}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.85rem",
            color: INK,
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
        {/* Collapsed: the group's current values. Expanded: the caller's own
            hint/action, which only means anything beside the controls. */}
        {!open && summary ? (
          <Typography
            noWrap
            sx={{
              flexGrow: 1,
              minWidth: 0,
              textAlign: "right",
              fontSize: "0.68rem",
              color: INK_MUTED,
            }}
          >
            {summary}
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
        )}
        {open && action}
        <ExpandMoreRounded
          sx={{
            fontSize: 19,
            color: INK_MUTED,
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s ease",
          }}
        />
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.5, py: 1.5 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

// API Service for Ticket Templates
const templateAPI = {
  // ticketType ("P"/"R"/"S") scopes the fetch — a store keeps a separate set
  // of templates (and one default) per ticket type.
  getTemplates: async (storeId, ticketType = "P", status = 1) => {
    const response = await axios.get(
      `${API_BASE_URL}/api/pawn/ticket-templates/store/${storeId}`,
      { params: { status, ticket_type: ticketType } },
    );
    return response.data;
  },
  getTemplate: async (templateId) => {
    const response = await axios.get(
      `${API_BASE_URL}/api/pawn/ticket-templates/${templateId}`,
    );
    return response.data;
  },
  createTemplate: async (storeId, templateData) => {
    const response = await axios.post(
      `${API_BASE_URL}/api/pawn/ticket-templates/store/${storeId}`,
      templateData,
    );
    return response.data;
  },
  updateTemplate: async (templateId, templateData) => {
    const response = await axios.put(
      `${API_BASE_URL}/api/pawn/ticket-templates/${templateId}`,
      templateData,
    );
    return response.data;
  },
  deleteTemplate: async (templateId) => {
    const response = await axios.delete(
      `${API_BASE_URL}/api/pawn/ticket-templates/${templateId}`,
    );
    return response.data;
  },
  resetToDefault: async (storeId, ticketType = "P") => {
    const response = await axios.post(
      `${API_BASE_URL}/api/pawn/ticket-templates/store/${storeId}/reset-default`,
      { ticket_type: ticketType },
    );
    return response.data;
  },
};

// Dialog Boxes
const DeleteConfirmDialog = ({ open, onClose, onConfirm, templateName }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <DeleteForever color="error" />
      Delete Template
    </DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to delete <strong>"{templateName}"</strong>?
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mt: 1, display: "block" }}
      >
        This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} color="error" variant="contained">
        Delete
      </Button>
    </DialogActions>
  </Dialog>
);
const ResetConfirmDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <RestartAlt color="warning" />
      Reset to Default
    </DialogTitle>
    <DialogContent>
      <Typography>
        Are you sure you want to reset to the system default template?
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mt: 1, display: "block" }}
      >
        This will replace your current layout with the system default template.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} color="warning" variant="contained">
        Reset
      </Button>
    </DialogActions>
  </Dialog>
);
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  confirmColor = "primary",
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 800, fontSize: "1.02rem", color: INK }}>
      {title}
    </DialogTitle>
    <DialogContent>
      <Typography>{message}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onConfirm} color={confirmColor} variant="contained">
        {confirmText}
      </Button>
    </DialogActions>
  </Dialog>
);

// Loans store a MONTHLY rate (e.g. 1.16 = 1.16% a month). Tickets print it per
// annum, which is how customers compare rates (1.16 → 13.92% per annum).
const annualRateText = (monthlyRate) => {
  const r = Number(monthlyRate) || 0;
  if (!r) return "—";
  return `${Number((r * 12).toFixed(2))}% per annum`;
};

// Print sources live here: laid out (so shrink-to-fit can measure) but never
// seen. `visibility` is not inherited by the print clone, which copies only
// the sheet itself.
const OFFSCREEN_STYLE = {
  position: "fixed",
  left: -20000,
  top: 0,
  pointerEvents: "none",
  visibility: "hidden",
};

// Shop details a template may replace with its own text (see storeOverrides).
const STORE_OVERRIDE_FIELDS = [
  { key: "name", label: "Shop Name" },
  { key: "phone", label: "Mobile / Phone" },
  { key: "address", label: "Address", multiline: true },
  { key: "email", label: "Email" },
  { key: "gst", label: "GSTIN" },
  { key: "pblId", label: "PBL No" },
];

const PawnTicketGenerator = () => {
  const location = useLocation();
  const ticketData = location.state;
  const currentStoreInfo = JSON.parse(
    localStorage.getItem("CapacitorStorage.current_store_info") || "{}",
  );
  const currentStoreID = currentStoreInfo.id;

  // Sample/Demo data for designer mode
  const sampleTransactionData = {
    ticketInfo: {
      voucherNo: "DEMO-001",
      date: new Date().toISOString().split("T")[0],
      time: new Date().toLocaleTimeString(),
    },
    customerInfo: {
      name: "Sample Customer",
      mobile: "9876543210",
      address: "Sample Address, City - 123456",
      aadhar: "1234-5678-9012",
      pan: "ABCDE1234F",
      sonOf: "Sample Father Name",
    },
    loanInfo: {
      loanAmount: 100000,
      itemValue: 120000,
      interestRate: 1.16, // monthly % — prints as 13.92% per annum
      interestAmount: 12000,
      scheme: "Gold Loan Scheme",
    },
    paymentInfo: {
      paymentAmount: 100000,
      cashAmount: 50000,
      bankAmount: 50000,
      paymentMethod: "Split Payment (Cash + Bank Transfer)",
      splitPayment: true,
      deductOneMonth: false,
    },
    ornaments: [
      {
        id: "1",
        description: "Gold Necklace",
        metal: "Gold",
        purity: "916",
        rate: 5500,
        pcs: 1,
        weight: 10.5,
        grossWeight: 11.0,
        value: 57750,
      },
      {
        id: "2",
        description: "Gold Earrings",
        metal: "Gold",
        purity: "916",
        rate: 5500,
        pcs: 2,
        weight: 5.2,
        grossWeight: 5.5,
        value: 28600,
      },
    ],
    summary: {
      totalOrnamentValue: 86350,
      totalLoanAmount: 100000,
      totalInterestAmount: 12000,
      totalPaymentAmount: 100000,
      dueAmount: 12000,
    },
  };

  // Determine if we're in designer mode (no real transaction data)
  const isDesignerMode =
    !ticketData || ticketData?.mode === "designer" || !ticketData?.loanInfo;

  // Use sample data if in designer mode, otherwise use real ticket data
  const effectiveData = {
    storeInfo: {
      name: currentStoreInfo.name || "",
      address: currentStoreInfo.address || "",
      phone: currentStoreInfo.owner_mobile || currentStoreInfo.phone || "",
      email: currentStoreInfo.owner_email || "",
      gst: currentStoreInfo.gst_no || "",
      pbl_id: currentStoreInfo.pbl_id || "",
      image: currentStoreInfo.image || "",
      storeId: currentStoreID,
    },
    ...(isDesignerMode ? sampleTransactionData : ticketData),
  };

  // ─── Ticket type (P = payment, R = receipt, S = settlement) ────────────────
  // Real tickets carry their type in the navigation state; designer mode lets
  // the user pick which type they're building a template for. The type decides
  // which set of templates loads and whether the ornaments table is replaced by
  // the receipt-amounts summary.
  const rawIncomingType = String(
    ticketData?.meta?.ticketType ||
      ticketData?.ticketType ||
      ticketData?.type ||
      "payment",
  ).toLowerCase();
  const incomingTicketTypeCode =
    ticketData?.meta?.ticketTypeCode ||
    (rawIncomingType.includes("settle") ||
    rawIncomingType.includes("redemption")
      ? "S"
      : rawIncomingType.includes("receipt") || rawIncomingType.includes("repay")
      ? "R"
      : "P");
  // In designer mode this is user-switchable; for a real ticket it never changes.
  const [activeTicketType, setActiveTicketType] = useState(
    incomingTicketTypeCode,
  );
  const isReceipt = activeTicketType === "R" || activeTicketType === "S";
  const isSettlement = activeTicketType === "S";

  const TICKET_TYPE_LABELS = { P: "Payment", R: "Receipt", S: "Settlement" };

  // Money breakdown shown on receipt/settlement tickets (in place of the
  // ornaments table). Real receipts carry it in effectiveData.receiptInfo;
  // designer mode falls back to demo figures so the layout is previewable.
  const receiptAmounts =
    effectiveData?.receiptInfo ||
    (isDesignerMode && isReceipt
      ? {
          interestPaid: 4500,
          interestDiscount: 500,
          insurancePaid: 300,
          otherCharges: 100,
          principalPaid: 20000,
          totalPaid: 24400,
          balanceBefore: 100000,
          balanceAfter: 80000,
          paymentMethod: "Cash",
          loanVoucherNo: "DEMO-LOAN-001",
        }
      : {});

  // Get storeId - from various possible sources
  const [storeId, setStoreId] = useState(() => {
    if (
      effectiveData?.meta?.storeId !== undefined &&
      effectiveData?.meta?.storeId !== null
    ) {
      return effectiveData.meta.storeId;
    }
    if (
      effectiveData?.storeInfo?.storeId !== undefined &&
      effectiveData?.storeInfo?.storeId !== null
    ) {
      return effectiveData.storeInfo.storeId;
    }
    // Try to get from localStorage as fallback
    const loggedUser = JSON.parse(
      localStorage.getItem("CapacitorStorage.user") || "{}",
    );
    return loggedUser?.store_id || null;
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState({
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Confirm",
    confirmColor: "primary",
  });

  // Paper size options
  const [paperSize, setPaperSize] = useState("A4");
  const [orientation, setOrientation] = useState("portrait");
  const [layoutTemplate, setLayoutTemplate] = useState("classic");
  const [fontSizeScale, setFontSizeScale] = useState(100);
  const [borderStyle, setBorderStyle] = useState("solid");
  const [borderColor, setBorderColor] = useState("#E2E8F0");
  const [borderWidth, setBorderWidth] = useState(1);
  const [primaryColor, setPrimaryColor] = useState("#2563EB");
  const [secondaryColor, setSecondaryColor] = useState("#64748B");
  const [showBorders, setShowBorders] = useState(true);
  const [tableStyle, setTableStyle] = useState("striped");
  const [headerAlignment, setHeaderAlignment] = useState("center");
  const [showWatermark, setShowWatermark] = useState(false);
  const [watermarkText, setWatermarkText] = useState("PAWN TICKET");
  const [showFooterMessage, setShowFooterMessage] = useState(false);
  const [footerMessage, setFooterMessage] = useState(
    "This is a computer generated document - No signature required",
  );
  // Top heading / invocation line printed centered at the very top of the
  // ticket (e.g. "॥ ॐ नमः शिवाय ॥"). Store-specific; empty by default so nothing
  // shows until set. Any language/script.
  const [showHeaderMessage, setShowHeaderMessage] = useState(false);
  const [headerText, setHeaderText] = useState("");
  const [margin, setMargin] = useState(10);
  // Two copies per transaction (one for the customer, one for the shop), each
  // captioned so they can't be mixed up. Turned off, a transaction prints a
  // SINGLE, uncaptioned ticket. Defaults on, so a template saved before this
  // setting existed keeps printing the two copies it always did.
  const [printTwoCopies, setPrintTwoCopies] = useState(true);
  // Both copies on ONE sheet, side by side: the paper is turned landscape and
  // split down the middle — Customer Copy on the left, Pawnee Copy on the
  // right, a dashed cut line between them. The template then designs a
  // half-sheet ticket (A5 → 105×148mm). Only meaningful with two copies.
  const [copiesSideBySide, setCopiesSideBySide] = useState(false);
  const twoUp = printTwoCopies && copiesSideBySide;
  // The customer already knows their own face: on request, the Customer Copy
  // carries only the ornaments photo while the Pawnee Copy keeps both.
  const [hideCustPhotoOnCustomerCopy, setHideCustPhotoOnCustomerCopy] =
    useState(false);
  // The physical sheet, and the ticket face the studio designs. One ticket per
  // sheet: the same thing. Side by side: the landscape sheet's left half.
  const sheetDimsMm = paperDims(paperSize, twoUp ? "landscape" : orientation);
  const ticketDimsMm = twoUp
    ? [sheetDimsMm[0] / 2, sheetDimsMm[1]]
    : sheetDimsMm;
  // Rich-text (HTML) Terms & Conditions — editable per store/template, prints
  // exactly as typed (supports any language/script).
  const [termsContent, setTermsContent] = useState(DEFAULT_TERMS_HTML);

  // ─── Free-form canvas layout ───────────────────────────────────────────────
  // A template is either "flow" (the stacked sections above) or "canvas": every
  // element absolutely placed and sized in mm by the client in the studio.
  // Both live in the same layout_config; the sections list is kept on a canvas
  // template so switching it back to flow loses nothing.
  const [layoutMode, setLayoutMode] = useState("flow");

  // ─── Per-copy layouts ──────────────────────────────────────────────────────
  // By default the Customer Copy and the Pawnee Copy print the same layout.
  // Unticking "Same layout for both copies" gives the Pawnee Copy its own
  // section list (flow) / element list (canvas), seeded from the Customer
  // Copy's. Styles, paper and shop details stay shared. The Customer Copy's
  // layout is the template's primary one (`sections`, `canvas.elements`);
  // the Pawnee Copy's rides beside it (`pawneeSections`, `canvas.pawneeElements`)
  // and is kept even while the copies are linked, so re-unticking restores it.
  const [copiesDiffer, setCopiesDiffer] = useState(false);
  const [activeCopy, setActiveCopy] = useState("customer");
  const copiesSplit = copiesDiffer && printTwoCopies;
  const editingPawnee = copiesSplit && activeCopy === "pawnee";
  const COPY_KEYS = ["customer", "pawnee"];

  // canvasHistory = Customer Copy (or the shared layout); pawneeHistory = the
  // Pawnee Copy's own. Everything the editor touches goes through the ACTIVE one.
  const canvasHistory = useCanvasHistory([]);
  const pawneeHistory = useCanvasHistory([]);
  const activeHistory = editingPawnee ? pawneeHistory : canvasHistory;
  const canvasElements = activeHistory.elements;
  const elementsForCopy = (copy) =>
    copy === "pawnee" && copiesSplit
      ? pawneeHistory.elements
      : canvasHistory.elements;
  const [canvasSide, setCanvasSide] = useState("front");
  const [canvasSelection, setCanvasSelection] = useState([]);
  const [focusTextTick, setFocusTextTick] = useState(0);
  const isCanvas = layoutMode === "canvas";
  // The ticket size (mm) the canvas elements were laid out for. When the
  // ticket size changes under them — paper size, orientation, or two copies
  // per sheet halving it — the layout is rescaled to the new size (see the
  // effect beside the canvas wiring) instead of spilling off the half sheet.
  const canvasDesignDims = useRef(null);

  // Template management
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [manageTemplatesOpen, setManageTemplatesOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Helper to get section title
  function getSectionTitle(id) {
    const titles = {
      header: "Heading / Invocation",
      store: "Store Details",
      ticketNo: "Ticket Number & Date",
      customer: "Customer Details",
      loan: "Loan Details",
      payment: "Payment Details",
      items: "Items Table",
      receiptSummary: "Receipt Amounts",
      signatures: "Signatures",
      terms: "Terms & Conditions",
      footer: "Footer Note",
    };
    return titles[id] || id;
  }

  // Which face of the sheet a section prints on. Anything marked "back" is
  // moved off the front page and printed on the REVERSE of the same sheet
  // (duplex) — typically the Terms & Conditions, which are long and would
  // otherwise squeeze the rest of the ticket. Missing/unknown values read as
  // "front", so templates saved before this feature keep printing one-sided.
  const normalizeSide = (side) => (side === "back" ? "back" : "front");

  // Build the {id,title,visible,side} objects for a list of section ids.
  const buildSections = (ids, visibleDefault = true) =>
    ids.map((id) => ({
      id,
      title: getSectionTitle(id),
      visible: visibleDefault,
      side: "front",
    }));

  // Guarantee a "terms" section toggle always exists (appended hidden if the
  // loaded template/layout doesn't already include it) so terms can be enabled
  // on ANY ticket type — clients may want local-language T&Cs on any ticket.
  const withTermsSection = (list) =>
    list.some((s) => s.id === "terms")
      ? list
      : [
          ...list,
          {
            id: "terms",
            title: getSectionTitle("terms"),
            visible: false,
            side: "front",
          },
        ];

  // Guarantee a "header" (heading / invocation) section always exists so it can
  // be dragged anywhere in the ticket. Some stores keep the invocation at the
  // very top; others use it as a closing note placed at the end of the ticket.
  // Prepended (top of the ticket) when a loaded template/layout omits it.
  const withHeaderSection = (list) =>
    list.some((s) => s.id === "header")
      ? list
      : [
          {
            id: "header",
            title: getSectionTitle("header"),
            visible: true,
            side: "front",
          },
          ...list,
        ];

  // The footer note is a section like any other (appended at the end when a
  // template predates this) so it can be reordered and pushed to the back of
  // the sheet too. It still renders only when the Extras toggle is on, so an
  // older template's output is unchanged.
  const withFooterSection = (list) =>
    list.some((s) => s.id === "footer")
      ? list
      : [
          ...list,
          {
            id: "footer",
            title: getSectionTitle("footer"),
            visible: true,
            side: "front",
          },
        ];

  // Every list handed to setSections goes through this, so the header/terms/
  // footer toggles and a valid `side` always exist regardless of where the list
  // came from (system default, saved template, quick layout).
  const withStandardSections = (list) =>
    withFooterSection(withHeaderSection(withTermsSection(list))).map((s) => ({
      ...s,
      side: normalizeSide(s.side),
    }));

  // Sections with visibility, order and sheet side
  const [customerSections, setCustomerSections] = useState(() =>
    withStandardSections(
      buildSections(LAYOUT_TEMPLATES[layoutTemplate].sections),
    ),
  );
  const [pawneeSections, setPawneeSections] = useState(null);
  // `sections` / `setSections` are the copy being EDITED — the Sections card,
  // quick layouts and the drag handlers all work on it unchanged.
  const sections =
    editingPawnee && pawneeSections ? pawneeSections : customerSections;
  const setSections = editingPawnee ? setPawneeSections : setCustomerSections;
  const sectionsForCopy = (copy) =>
    copy === "pawnee" && copiesSplit && pawneeSections
      ? pawneeSections
      : customerSections;

  // Initialize state from effectiveData (not ticketData directly)
  // What the store master says (owner mobile, address…). The ticket prints
  // `storeDetails` below, which is this with the template's overrides applied.
  const [storeDetailsBase] = useState({
    name: effectiveData?.storeInfo?.name || "",
    address: effectiveData?.storeInfo?.address || "",
    phone: effectiveData?.storeInfo?.phone || "",
    email: effectiveData?.storeInfo?.email || "",
    gst: effectiveData?.storeInfo?.gst || "",
    logo: effectiveData?.storeInfo?.logo || null,
    image: effectiveData?.storeInfo?.image || currentStoreInfo.image || "",
    pblId: currentStoreInfo.pbl_id || "",
  });

  // Per-template shop details for the ticket. Some shops print a counter
  // number / branch address that differs from the owner's login mobile and the
  // address on the store master. Each field is either the store's own data
  // (default) or custom text: { phone: { custom: true, value: "…" } }. The
  // typed value is kept when switched back to store data, so toggling never
  // loses it. Custom + empty = that line is not printed.
  const [storeOverrides, setStoreOverrides] = useState({});
  const storeDetails = { ...storeDetailsBase };
  STORE_OVERRIDE_FIELDS.forEach(({ key }) => {
    const o = storeOverrides[key];
    if (o?.custom) storeDetails[key] = o.value || "";
  });

  const [ticketDetails, setTicketDetails] = useState({
    voucherNo: effectiveData?.ticketInfo?.voucherNo || "",
    date:
      effectiveData?.ticketInfo?.date || new Date().toISOString().split("T")[0],
    time: effectiveData?.ticketInfo?.time || "",
  });

  const [customerDetails, setCustomerDetails] = useState({
    name: effectiveData?.customerInfo?.name || "",
    mobile: effectiveData?.customerInfo?.mobile || "",
    address: effectiveData?.customerInfo?.address || "",
    aadhar: effectiveData?.customerInfo?.aadhar || "",
    pan: effectiveData?.customerInfo?.pan || "",
    sonOf: effectiveData?.customerInfo?.sonOf || "",
    ledgerNo: effectiveData?.customerInfo?.ledgerNo || "",
  });

  // Customer & ornament photos shown in the Customer Details section. Real data
  // carries them in effectiveData.images; in designer mode we fall back to the
  // dummy avatar/star placeholders so the layout is visible and editable.
  const [customerImage] = useState(
    effectiveData?.images?.customer ||
      (isDesignerMode ? DUMMY_CUSTOMER_IMG : null),
  );
  const [ornamentImage] = useState(
    effectiveData?.images?.ornament ||
      (isDesignerMode ? DUMMY_ORNAMENT_IMG : null),
  );

  const [loanDetails, setLoanDetails] = useState({
    principal: effectiveData?.loanInfo?.loanAmount || 0,
    itemValue: effectiveData?.loanInfo?.itemValue || 0,
    interestRate: effectiveData?.loanInfo?.interestRate || 0,
    interestAmount: effectiveData?.loanInfo?.interestAmount || 0,
    insuranceRate: effectiveData?.loanInfo?.insuranceRate || 0,
    insuranceAmount: effectiveData?.loanInfo?.insuranceAmount || 0,
    scheme: effectiveData?.loanInfo?.scheme || "",
    collectByDate: effectiveData?.loanInfo?.collectByDate || "",
    remarks: effectiveData?.loanInfo?.remarks || "",
  });

  const [paymentDetails, setPaymentDetails] = useState({
    paymentAmount: effectiveData?.paymentInfo?.paymentAmount || 0,
    cashAmount: effectiveData?.paymentInfo?.cashAmount || 0,
    bankAmount: effectiveData?.paymentInfo?.bankAmount || 0,
    paymentMethod: effectiveData?.paymentInfo?.paymentMethod || "",
    chequeNo: effectiveData?.paymentInfo?.chequeNo || "",
    splitPayment: effectiveData?.paymentInfo?.splitPayment || false,
    deductOneMonth: effectiveData?.paymentInfo?.deductOneMonth || false,
  });

  const [items, setItems] = useState(() => {
    if (effectiveData?.ornaments && effectiveData.ornaments.length > 0) {
      return effectiveData.ornaments.map((item) => ({
        id: item.id?.toString() || Math.random().toString(),
        description: item.description || "",
        metal: item.metal || "",
        purity: item.purity || "",
        rate: item.rate || 0,
        pcs: item.pcs || 1,
        weight: item.weight || 0,
        grossWeight: item.grossWeight || 0,
        value: item.value || 0,
      }));
    }
    return [];
  });

  const [summary, setSummary] = useState({
    totalOrnamentValue: effectiveData?.summary?.totalOrnamentValue || 0,
    totalLoanAmount: effectiveData?.summary?.totalLoanAmount || 0,
    totalInterestAmount: effectiveData?.summary?.totalInterestAmount || 0,
    totalPaymentAmount: effectiveData?.summary?.totalPaymentAmount || 0,
    dueAmount: effectiveData?.summary?.dueAmount || 0,
  });

  const [nomineeInfo, setNomineeInfo] = useState(
    effectiveData?.nomineeInfo || null,
  );

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Sensors for drag-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // A saved section list → the studio's section rows.
  // Adopts the list wholesale — ids, order AND visibility. (Previously only
  // visibility was toggled on the existing set, so a template's ordering and
  // any new section id like "receiptSummary" were silently dropped.)
  const sectionsFromConfig = (list) =>
    withStandardSections(
      list.map((s) => ({
        id: s.id,
        title: getSectionTitle(s.id),
        visible: s.visible !== false,
        side: s.side,
      })),
    );
  const sectionsToConfig = (list) =>
    list.map((s) => ({
      id: s.id,
      visible: s.visible,
      side: normalizeSide(s.side),
    }));

  // Apply template configuration
  const applyTemplateConfig = (config) => {
    setCopiesDiffer(!!config.copiesDiffer);
    setActiveCopy("customer");
    setPaperSize(config.paperSize || "A4");
    setOrientation(config.orientation || "portrait");
    setFontSizeScale(config.fontSizeScale || 100);
    setBorderStyle(config.borderStyle || "solid");
    setBorderColor(config.borderColor || "#E2E8F0");
    setBorderWidth(config.borderWidth || 1);
    setPrimaryColor(config.primaryColor || "#2563EB");
    setSecondaryColor(config.secondaryColor || "#64748B");
    setShowBorders(
      config.showBorders !== undefined ? config.showBorders : true,
    );
    setTableStyle(config.tableStyle || "striped");
    setHeaderAlignment(config.headerAlignment || "center");
    setShowWatermark(config.showWatermark || false);
    setWatermarkText(config.watermarkText || "PAWN TICKET");
    setShowFooterMessage(
      config.showFooterMessage !== undefined ? config.showFooterMessage : true,
    );
    setFooterMessage(
      config.footerMessage ||
        "This is a computer generated document - No signature required",
    );
    setMargin(config.margin || 10);
    setPrintTwoCopies(
      config.printTwoCopies !== undefined ? config.printTwoCopies : true,
    );
    setCopiesSideBySide(config.copiesPerSheet === 2);
    setHideCustPhotoOnCustomerCopy(!!config.hideCustomerPhotoOnCustomerCopy);
    setShowHeaderMessage(
      config.showHeaderMessage !== undefined ? config.showHeaderMessage : false,
    );
    setHeaderText(config.headerText || "");
    setTermsContent(config.termsContent || DEFAULT_TERMS_HTML);
    setStoreOverrides(
      config.storeOverrides && typeof config.storeOverrides === "object"
        ? config.storeOverrides
        : {},
    );
    setLayoutMode(config.mode === "canvas" ? "canvas" : "flow");
    canvasHistory.reset(normalizeElements(config.canvas?.elements));
    pawneeHistory.reset(normalizeElements(config.canvas?.pawneeElements));
    // Size the saved layout was made for: stored since this version; older
    // templates are assumed to match the ticket size their own settings give.
    {
      const cTwoUp =
        config.printTwoCopies !== false && config.copiesPerSheet === 2;
      const [sw, sh] = paperDims(
        config.paperSize || "A4",
        cTwoUp ? "landscape" : config.orientation || "portrait",
      );
      canvasDesignDims.current =
        config.canvas?.paperW && config.canvas?.paperH
          ? [config.canvas.paperW, config.canvas.paperH]
          : cTwoUp
          ? [sw / 2, sh]
          : [sw, sh];
    }
    setCanvasSelection([]);
    setCanvasSide("front");

    if (config.sections && Array.isArray(config.sections)) {
      // Adopt the template's section list wholesale — its ids, order AND
      // visibility. (Previously this only toggled visibility on the existing
      // section set, so a template's ordering and any new section id like
      // "receiptSummary" were silently dropped.)
      setCustomerSections(sectionsFromConfig(config.sections));
    }
    setPawneeSections(
      Array.isArray(config.pawneeSections)
        ? sectionsFromConfig(config.pawneeSections)
        : null,
    );
  };

  // Get current layout config
  const getCurrentLayoutConfig = () => {
    return {
      paperSize,
      orientation,
      fontSizeScale,
      borderStyle,
      borderColor,
      borderWidth,
      primaryColor,
      secondaryColor,
      showBorders,
      tableStyle,
      headerAlignment,
      showWatermark,
      watermarkText,
      showFooterMessage,
      footerMessage,
      margin,
      printTwoCopies,
      copiesPerSheet: copiesSideBySide ? 2 : 1,
      hideCustomerPhotoOnCustomerCopy: hideCustPhotoOnCustomerCopy,
      showHeaderMessage,
      headerText,
      termsContent,
      // Customer Copy (or the shared layout when the copies are linked).
      sections: sectionsToConfig(customerSections),
      copiesDiffer,
      ...(pawneeSections
        ? { pawneeSections: sectionsToConfig(pawneeSections) }
        : {}),
      storeOverrides,
      mode: layoutMode,
      // Saved even in flow mode, so flipping a template to flow and back keeps
      // the client's hand-placed layout.
      ...(canvasHistory.elements.length || pawneeHistory.elements.length
        ? {
            canvas: {
              version: CANVAS_VERSION,
              paperW: ticketDimsMm[0],
              paperH: ticketDimsMm[1],
              elements: canvasHistory.elements,
              ...(pawneeHistory.elements.length
                ? { pawneeElements: pawneeHistory.elements }
                : {}),
            },
          }
        : {}),
    };
  };

  // Load templates from database on mount, and whenever the active ticket type
  // changes (designer mode lets the user switch P/R/S).
  useEffect(() => {
    if (storeId !== undefined && storeId !== null) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, activeTicketType]);

  // If in designer mode and no templates loaded, apply default config
  useEffect(() => {
    if (isDesignerMode && savedTemplates.length === 0 && !loading) {
      const defaultConfig = {
        paperSize: "A4",
        orientation: "portrait",
        fontSizeScale: 100,
        borderStyle: "solid",
        borderColor: "#E2E8F0",
        borderWidth: 1,
        primaryColor: "#2563EB",
        secondaryColor: "#64748B",
        showBorders: true,
        tableStyle: "striped",
        headerAlignment: "center",
        showWatermark: false,
        watermarkText: "PAWN TICKET",
        showFooterMessage: true,
        footerMessage:
          "This is a computer generated document - No signature required",
        margin: 10,
        // Fallback section set matches the active ticket type: receipt/settlement
        // drop the ornaments table for the receipt-amounts summary.
        sections: (isReceipt
          ? [
              "store",
              "ticketNo",
              "customer",
              "loan",
              "receiptSummary",
              "signatures",
            ]
          : LAYOUT_TEMPLATES.classic.sections
        ).map((id) => ({
          id,
          visible: true,
        })),
      };
      applyTemplateConfig(defaultConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesignerMode, savedTemplates.length, loading, isReceipt]);

  // Load templates function
  const loadTemplates = async () => {
    if (storeId === undefined || storeId === null) return;

    setLoading(true);
    try {
      const response = await templateAPI.getTemplates(
        storeId,
        activeTicketType,
      );
      if (response.success) {
        setSavedTemplates(response.data);

        const defaultTemplate = response.data.find((t) => t.is_default);
        if (defaultTemplate) {
          setCurrentTemplateId(defaultTemplate.id);
          applyTemplateConfig(defaultTemplate.layout_config);
        } else if (response.data.length > 0) {
          setCurrentTemplateId(response.data[0].id);
          applyTemplateConfig(response.data[0].layout_config);
        }
      }
    } catch (error) {
      console.error("Error loading templates:", error);
      showSnackbar("Failed to load templates", "error");
    } finally {
      setLoading(false);
    }
  };

  // Save current layout as template
  const saveCurrentTemplate = async () => {
    if (!templateName.trim()) {
      showSnackbar("Please enter a template name", "warning");
      return;
    }

    if (storeId === undefined || storeId === null) {
      showSnackbar(
        "Store ID not found. Please refresh and try again.",
        "error",
      );
      return;
    }

    setLoading(true);
    try {
      const layoutConfig = getCurrentLayoutConfig();

      const response = await templateAPI.createTemplate(storeId, {
        template_name: templateName,
        template_description: templateDescription,
        layout_config: layoutConfig,
        set_as_default: setAsDefault,
        ticket_type: activeTicketType, // P / R / S
        status: 1, // Default status
      });

      if (response.success) {
        setSavedTemplates((prev) => [...prev, response.data]);
        setCurrentTemplateId(response.data.id);
        showSnackbar(
          `Template "${templateName}" saved successfully`,
          "success",
        );
        setTemplateDialogOpen(false);
        setTemplateName("");
        setTemplateDescription("");
        setSetAsDefault(false);
      }
    } catch (error) {
      console.error("Save template error:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to save template",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Update existing template
  const updateCurrentTemplate = async () => {
    if (!currentTemplateId) {
      showSnackbar("No template selected to update", "warning");
      return;
    }

    setLoading(true);
    try {
      const layoutConfig = getCurrentLayoutConfig();

      const response = await templateAPI.updateTemplate(currentTemplateId, {
        layout_config: layoutConfig,
      });

      if (response.success) {
        setSavedTemplates((prev) =>
          prev.map((t) => (t.id === currentTemplateId ? response.data : t)),
        );
        showSnackbar("Template updated successfully", "success");
      }
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to update template",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Load template
  const loadTemplate = async (template) => {
    setLoading(true);
    try {
      const response = await templateAPI.getTemplate(template.id);
      if (response.success) {
        setCurrentTemplateId(response.data.id);
        applyTemplateConfig(response.data.layout_config);
        showSnackbar(
          `Template "${response.data.template_name}" loaded`,
          "success",
        );
        setManageTemplatesOpen(false);
      }
    } catch (error) {
      showSnackbar("Failed to load template", "error");
    } finally {
      setLoading(false);
    }
  };

  // Delete template - Updated with dialog
  const deleteTemplate = async (template) => {
    if (template.is_system) {
      showSnackbar("System templates cannot be deleted", "warning");
      return;
    }

    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    setLoading(true);
    try {
      await templateAPI.deleteTemplate(templateToDelete.id);
      setSavedTemplates((prev) =>
        prev.filter((t) => t.id !== templateToDelete.id),
      );

      if (currentTemplateId === templateToDelete.id) {
        const defaultTemplate = savedTemplates.find(
          (t) => t.id !== templateToDelete.id && t.is_default,
        );
        if (defaultTemplate) {
          loadTemplate(defaultTemplate);
        } else if (savedTemplates.length > 1) {
          const remainingTemplate = savedTemplates.find(
            (t) => t.id !== templateToDelete.id,
          );
          if (remainingTemplate) {
            loadTemplate(remainingTemplate);
          }
        }
      }
      showSnackbar(
        `Template "${templateToDelete.template_name}" deleted`,
        "success",
      );
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Failed to delete template",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Set as default template
  const setAsDefaultTemplate = async (template) => {
    setLoading(true);
    try {
      const response = await templateAPI.updateTemplate(template.id, {
        set_as_default: true,
      });

      if (response.success) {
        setSavedTemplates((prev) =>
          prev.map((t) => ({
            ...t,
            is_default: t.id === template.id,
          })),
        );
        showSnackbar(`"${template.template_name}" set as default`, "success");
      }
    } catch (error) {
      showSnackbar("Failed to set as default", "error");
    } finally {
      setLoading(false);
    }
  };

  // Reset to system default - Updated with dialog
  const resetToSystemDefault = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = async () => {
    if (storeId === undefined || storeId === null) {
      showSnackbar("Store ID not found", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await templateAPI.resetToDefault(
        storeId,
        activeTicketType,
      );
      if (response.success) {
        setCurrentTemplateId(response.data.id);
        applyTemplateConfig(response.data.layout_config);
        await loadTemplates();
        showSnackbar("Reset to system default template", "success");
      }
    } catch (error) {
      console.error("Reset error:", error);
      showSnackbar("Failed to reset to default", "error");
    } finally {
      setLoading(false);
      setResetDialogOpen(false);
    }
  };

  // Apply layout template
  const applyLayoutTemplate = (templateId) => {
    const template = LAYOUT_TEMPLATES[templateId];
    if (template) {
      setLayoutTemplate(templateId);
      // A quick layout is a fresh start: every section returns to the front of
      // the sheet, so pick the layout first and then push blocks to the back.
      setSections(withStandardSections(buildSections(template.sections)));
      showSnackbar(`${template.name} layout applied`, "success");
    }
  };

  // Toggle section visibility
  const toggleSection = (id) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s)),
    );
  };

  // Flip a section between the front and the back of the sheet.
  const toggleSectionSide = (id) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, side: normalizeSide(s.side) === "back" ? "front" : "back" }
          : s,
      ),
    );
  };

  // Handle drag end — one handler per side, since the two faces of the sheet are
  // dragged as separate lists. Only the dragged side's slots are rewritten: the
  // reordered ids are dropped back into the positions that side already occupied
  // in the master list, so the other face's order is untouched.
  const handleSideDragEnd = (side) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const slots = prev.reduce((acc, s, i) => {
        if (normalizeSide(s.side) === side) acc.push(i);
        return acc;
      }, []);
      const ids = slots.map((i) => prev[i].id);
      const from = ids.indexOf(active.id);
      const to = ids.indexOf(over.id);
      if (from < 0 || to < 0) return prev;
      const moved = arrayMove(ids, from, to);
      const next = [...prev];
      slots.forEach((slotIndex, k) => {
        next[slotIndex] = prev.find((s) => s.id === moved[k]);
      });
      return next;
    });
  };

  // Items handlers
  const addItem = () => {
    setItems((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        description: "",
        metal: "",
        purity: "",
        rate: 0,
        pcs: 1,
        weight: 0,
        grossWeight: 0,
        value: 0,
      },
    ]);
  };

  const updateItem = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  };

  const deleteItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Get table styles based on selection
  const getTableStyles = () => {
    const styles = {
      striped: {
        headerBg: "#f5f5f5",
        rowEvenBg: "#ffffff",
        rowOddBg: "#fafafa",
        borderBottom: "1px solid #e0e0e0",
      },
      bordered: {
        headerBg: "#f5f5f5",
        rowEvenBg: "#ffffff",
        rowOddBg: "#ffffff",
        borderBottom: "1px solid #ddd",
        border: "1px solid #ddd",
      },
      minimal: {
        headerBg: "transparent",
        rowEvenBg: "#ffffff",
        rowOddBg: "#ffffff",
        borderBottom: "none",
      },
    };
    return styles[tableStyle];
  };

  // With the "2 copies" setting on, a transaction prints one ticket for the
  // customer and one for the shop, each captioned in its top-right corner so the
  // two can never be mixed up. Order matters — the customer's copy comes off
  // first. With it off a single ticket prints, and a lone ticket needs no caption
  // (there is nothing to tell it apart from), so the label is dropped entirely.
  const PRINT_COPIES = printTwoCopies
    ? ["Customer Copy", "Pawnee Copy"]
    : [null];

  // Print handler
  // Every stylesheet's rules as inline <style> tags for the print window. Reads
  // cssRules rather than copying <style>.outerHTML — see the note in
  // handlePrint for why (Emotion's production "speedy" mode).
  const collectPrintStyles = () => {
    let styleHTML = "";
    Array.from(document.styleSheets).forEach((sheet) => {
      try {
        const rules = sheet.cssRules; // throws for cross-origin sheets
        if (!rules) return;
        let cssText = "";
        for (let i = 0; i < rules.length; i++) {
          cssText += rules[i].cssText;
        }
        if (cssText) {
          styleHTML += `<style>${cssText}</style>`;
        }
      } catch (e) {
        // Cross-origin stylesheet (e.g. Google Fonts) — link it instead so the
        // print window can load it directly.
        if (sheet.href) {
          styleHTML += `<link rel="stylesheet" href="${sheet.href}" />`;
        }
      }
    });
    return styleHTML;
  };

  // One copy's markup. The Customer Copy (and its reverse) loses the
  // customer's photo when the template asks for it; every other copy prints
  // the sheet unchanged.
  const isCustomerCopy = (label) =>
    !!label && printTwoCopies && label.startsWith(PRINT_COPIES[0]);
  const htmlForCopy = (html, label) => {
    if (!hideCustPhotoOnCustomerCopy || !isCustomerCopy(label)) return html;
    const t = document.createElement("template");
    t.innerHTML = html;
    t.content.querySelectorAll(".tc-cust-photo").forEach((n) => n.remove());
    return t.innerHTML;
  };

  // Free-form ticket print. The page IS the canvas: @page has no margin and each
  // page is the full paper in mm, so every element lands exactly where it was
  // placed. Shrink-to-fit scales were measured on the off-screen print sources
  // and ride along inline on the clone. The copy caption element's text is
  // swapped per copy.
  const printCanvasTicket = () => {
    // Each copy prints from its own off-screen source sheets (the copies may
    // have different layouts — see renderCanvasPrintSources).
    const node = (copy, side) =>
      document.getElementById(`tc-print-${copy}-${side}`);
    const pageHTML = (src, label) => {
      if (!src) return ""; // a copy with no reverse: blank back keeps duplex paired
      const clone = src.cloneNode(true);
      clone.removeAttribute("id");
      clone.style.boxShadow = "none";
      clone.style.transform = "none";
      clone.querySelectorAll(".tc-editor-only").forEach((n) => n.remove());
      clone.querySelectorAll(`.${COPY_LABEL_CLASS}`).forEach((n) => {
        n.textContent = label || "";
      });
      return htmlForCopy(clone.outerHTML, label);
    };
    const faces = PRINT_COPIES.map((label, i) => {
      const copy = COPY_KEYS[i] || "customer";
      return { label, front: node(copy, "front"), back: node(copy, "back") };
    });
    if (!faces[0].front) return;
    const anyBack = faces.some((face) => face.back);
    const reverse = (label) => (label ? `${label} · Reverse` : "");
    if (twoUp) {
      printTwoUpSheets({
        sheets: [
          faces.map((face) => pageHTML(face.front, face.label)),
          ...(anyBack
            ? [faces.map((face) => pageHTML(face.back, reverse(face.label)))]
            : []),
        ],
        padMm: 0,
        flow: false,
      });
      return;
    }
    const pages = faces.flatMap((face) => [
      pageHTML(face.front, face.label),
      ...(anyBack ? [pageHTML(face.back, reverse(face.label))] : []),
    ]);
    const title = `Pawn Ticket ${ticketDetails.voucherNo || ""}`.trim();
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <meta charset="UTF-8" />
          ${collectPrintStyles()}
          <style>
            @page { size: ${canvasPaperW}mm ${canvasPaperH}mm; margin: 0; }
            html, body { margin: 0; padding: 0; background: #fff; }
            .tc-page {
              width: ${canvasPaperW}mm;
              height: ${canvasPaperH}mm;
              overflow: hidden;
              position: relative;
            }
            .tc-page + .tc-page { break-before: page; page-break-before: always; }
            .tc-page > .tc-sheet { position: absolute !important; left: 0; top: 0; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          </style>
        </head>
        <body>
          ${pages.map((html) => `<div class="tc-page">${html}</div>`).join("")}
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          <\/script>
        </body>
      </html>`);
    printWindow.document.close();
  };

  // Two copies on ONE landscape sheet: Customer Copy on the left half, Pawnee
  // Copy on the right, a dashed cut line down the middle. `sheets` is one entry
  // per printed page, each [leftHTML, rightHTML]. A two-sided template adds a
  // page of backs in the SAME left/right order, which is what "flip on long
  // edge" duplex needs to land each back behind its own front.
  //   padMm — inner margin of each half (flow layout; the canvas is already
  //           laid out in exact mm, so 0 there)
  //   flow  — the halves carry a copy caption + the classic fit-to-box shrink
  const printTwoUpSheets = ({ sheets, padMm, flow }) => {
    const [sw, sh] = sheetDimsMm;
    const half = sw / 2;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    const hasBack = sheets.length > 1;
    printWindow.document.write(`<!DOCTYPE html>
      <html>
        <head>
          <title>Pawn Ticket ${ticketDetails.voucherNo || ""}</title>
          <meta charset="UTF-8" />
          ${collectPrintStyles()}
          <style>
            @page { size: ${sw}mm ${sh}mm; margin: 0; }
            html, body { margin: 0; padding: 0; background: #fff; }
            body { font-family: 'Roboto', 'Inter', sans-serif; font-size: ${fontSizeScale}%; }
            .tu-sheet {
              width: ${sw}mm; height: ${sh}mm;
              position: relative; display: flex; overflow: hidden;
            }
            .tu-sheet + .tu-sheet { break-before: page; page-break-before: always; }
            .tu-half {
              width: ${half}mm; height: ${sh}mm; flex: none;
              box-sizing: border-box; padding: ${padMm}mm;
              position: relative; overflow: hidden;
            }
            .tu-body { position: relative; height: 100%; box-sizing: border-box; ${
              flow ? "padding-top: 14px;" : ""
            } }
            .tu-half > .tc-sheet { position: absolute !important; left: 0; top: 0; }
            ${
              flow
                ? `/* Half a sheet has no room for full-page spacing: tighten the
                     gaps between sections and the photo sizes so the whole
                     ticket — down to the signatures and footer — fits at a
                     readable scale. */
                  .tu-body .pawn-ticket-preview > .MuiBox-root > .MuiBox-root {
                    margin-bottom: 4px !important;
                    padding: 6px 8px !important;
                  }
                  .tu-body .pawn-ticket-preview .MuiDivider-root {
                    margin-top: 3px !important; margin-bottom: 3px !important;
                  }
                  .tu-body .pawn-ticket-preview img {
                    max-height: 72px !important; width: auto !important;
                  }`
                : ""
            }
            /* The cut line: thin, dashed, light grey — visible enough to tear
               along, faint enough not to look like part of either ticket. */
            .tu-cut {
              position: absolute; top: 0; bottom: 0; left: ${half}mm; width: 0;
              border-left: 0.25mm dashed #94A3B8; z-index: 10;
            }
            .tu-cut::before {
              content: "\\2702"; position: absolute; top: 2mm; left: -1.9mm;
              font-size: 9px; line-height: 1; color: #94A3B8; background: #fff;
              transform: rotate(90deg);
            }
            .copy-label {
              position: absolute; top: 0; right: 0; z-index: 5;
              font-size: 8px; font-weight: 700; letter-spacing: 1.2px;
              text-transform: uppercase; color: #334155;
              border: 1px solid #94A3B8; border-radius: 3px;
              padding: 1px 5px; background: #fff; white-space: nowrap;
            }
            .duplex-note {
              max-width: ${sw}mm; margin: 0 auto 6mm; padding: 8px 12px;
              border: 1px solid #F59E0B; border-radius: 4px; background: #FFFBEB;
              color: #92400E; font-size: 12px; line-height: 1.4;
            }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            @media print { .duplex-note { display: none; } }
          </style>
        </head>
        <body>
          ${
            hasBack
              ? `<div class="duplex-note"><b>Two-sided ticket.</b> Turn on
                 <b>Print on both sides → Flip on long edge</b> in the print
                 dialog so each reverse lands behind its own copy.</div>`
              : ""
          }
          ${sheets
            .map(
              (pair) => `<div class="tu-sheet">
                ${pair
                  .map((html) => `<div class="tu-half">${html}</div>`)
                  .join("")}
                <div class="tu-cut"></div>
              </div>`,
            )
            .join("")}
          <script>
            window.onload = () => {
              ${
                flow
                  ? `// Shrink a copy that is too tall for its half. It re-wraps
                    // wider as it shrinks, so search for the largest scale that
                    // fits rather than taking one ratio.
                    document.querySelectorAll(".tu-body").forEach((box) => {
                      const body = box.querySelector(".pawn-ticket-preview");
                      if (!body) return;
                      const base = parseFloat(body.dataset.baseScale) || 1;
                      const reserved = parseFloat(getComputedStyle(box).paddingTop) || 0;
                      const avail = box.clientHeight - reserved;
                      const fits = (s) => {
                        body.style.width = (100 / s) + "%";
                        body.style.transform = "scale(" + s + ")";
                        return body.getBoundingClientRect().height <= avail + 0.5;
                      };
                      if (avail <= 0 || fits(base)) return;
                      // No floor that can clip: the whole copy always fits.
                      let lo = 0.2, hi = base;
                      for (let i = 0; i < 8; i++) {
                        const mid = (lo + hi) / 2;
                        if (fits(mid)) lo = mid; else hi = mid;
                      }
                      fits(lo);
                    });`
                  : ""
              }
              window.print();
              window.onafterprint = () => window.close();
            };
          <\/script>
        </body>
      </html>`);
    printWindow.document.close();
  };

  const handlePrint = () => {
    if (isCanvas) {
      printCanvasTicket();
      return;
    }

    // Turn a preview sheet into printable markup.
    const toPrintableHTML = (node) => {
      const clone = node.cloneNode(true);
      // The on-screen preview may be shrunk to fit a narrow panel via `zoom`
      // (see previewScale). That class rides along in the copied stylesheets, so
      // override it inline on the clone — otherwise a phone/narrow-window print
      // comes out scaled down. Same for the scroll box and drop shadow.
      clone.style.zoom = "1";
      clone.style.boxShadow = "none";
      clone.style.overflowY = "visible";
      // The preview node is sized as a physical sheet (21cm × 29.7cm for A4)
      // with its own padding. On paper the @page rule already provides both, so
      // keeping them would push each copy past the printable area and spill a
      // near-blank second sheet after every copy. Let the content flow inside
      // the page box.
      clone.style.width = "100%";
      clone.style.minHeight = "0";
      clone.style.padding = "0";
      // The studio sheet animates size changes; on paper that would make the
      // fit-to-sheet script below measure the size from BEFORE its own change.
      clone.style.transition = "none";
      if (twoUp) {
        // Same design-width trick as the preview (flowHalfScale), done with a
        // transform so the print window's fit script can measure and refine it.
        clone.style.width = `${100 / flowHalfScale}%`;
        clone.style.transform = `scale(${flowHalfScale})`;
        clone.style.transformOrigin = "top left";
        clone.dataset.baseScale = String(flowHalfScale);
      }
      // The clone is emitted several times, so it must not carry the preview id.
      clone.removeAttribute("id");
      return clone.outerHTML;
    };

    // Each copy prints from its own off-screen source sheets (see
    // renderFlowPrintSources) — the copies may have different layouts, and the
    // on-screen preview may be showing only one of them.
    const sourceHTML = (copy, side) => {
      const node = document.getElementById(`tp-${copy}-${side}`);
      return node ? toPrintableHTML(node) : null;
    };
    const faces = PRINT_COPIES.map((label, i) => {
      const copy = COPY_KEYS[i] || "customer";
      return {
        label,
        front: sourceHTML(copy, "front"),
        back: sourceHTML(copy, "back"),
      };
    });
    if (!faces[0].front) return;
    // Two-sided if either copy has a back; a copy without one gets a blank
    // reverse so the duplex pairing of the pages stays intact.
    const anyBack = faces.some((face) => face.back);

    if (twoUp) {
      const halfHTML = (label, html) =>
        html
          ? `<div class="tu-body"><div class="copy-label">${label}</div>${htmlForCopy(
              html,
              label,
            )}</div>`
          : "";
      printTwoUpSheets({
        sheets: [
          faces.map((face) => halfHTML(face.label, face.front)),
          ...(anyBack
            ? [
                faces.map((face) =>
                  halfHTML(`${face.label} · Reverse`, face.back),
                ),
              ]
            : []),
        ],
        // A half sheet is small; cap the inner margin so the ticket keeps room.
        padMm: Math.min(margin, 6),
        flow: true,
      });
      return;
    }

    // Build the print stylesheet from the actual CSS rules of every stylesheet.
    //
    // NOTE: We must NOT rely on copying <style>.outerHTML. MUI uses Emotion,
    // which in DEVELOPMENT injects CSS as text inside <style> tags, but in
    // PRODUCTION builds runs in "speedy" mode and inserts rules directly via
    // CSSStyleSheet.insertRule(), leaving the <style> tags EMPTY. Copying
    // outerHTML in production therefore loses all MUI styling and the layout
    // collapses (everything left-aligned). Reading `cssRules` returns the real
    // rules in both modes.
    const styleHTML = collectPrintStyles();

    // Printable area of one sheet, in mm: the paper minus the @page margins.
    // Each copy is boxed to exactly this, so "one copy = one sheet" holds even
    // when a template runs a little long (see the fit-to-sheet script below).
    const sheetMm = paperSize === "A4" ? [210, 297] : [148, 210];
    const [sheetW, sheetH] =
      orientation === "portrait" ? sheetMm : [sheetMm[1], sheetMm[0]];
    const copyWidthMm = sheetW - margin * 2;
    // 3mm of slack absorbs mm→px rounding, which would otherwise be enough to
    // tip a full-height copy onto a second, near-empty page.
    const copyHeightMm = sheetH - margin * 2 - 3;

    // The pages of the job, in the order the printer pulls them. A copy's front
    // and back are ADJACENT pages, which is what a duplex printer needs to land
    // them on the two faces of one physical sheet — customer front, customer
    // back, pawnee front, pawnee back. A one-sided template emits just the
    // two front pages, exactly as before.
    const pages = faces.flatMap((face) => [
      { label: face.label, html: face.front },
      ...(anyBack
        ? [
            {
              label: face.label ? `${face.label} · Reverse` : null,
              html: face.back || "",
            },
          ]
        : []),
    ]);

    const originalTitle = document.title;
    document.title = `Pawn_Ticket_${ticketDetails.voucherNo}`;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Pawn Ticket ${ticketDetails.voucherNo}</title>
            <meta charset="UTF-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            ${styleHTML}
            <style>
              body {
                margin: 0;
                padding: ${margin}mm;
                background: white;
                font-family: 'Roboto', 'Inter', sans-serif;
                font-size: ${fontSizeScale}%;
              }
              .print-ticket {
                /* One copy = exactly one sheet's printable area. */
                width: ${copyWidthMm}mm;
                height: ${copyHeightMm}mm;
                max-width: 100%;
                margin: 0 auto;
                background: white;
                /* Visible, not hidden: if a template is so long that even the
                   0.7 scale floor can't fit it, spilling is better than
                   silently clipping terms off the bottom of the ticket. */
                overflow: visible;
                /* Anchor for the copy caption badge below, plus a reserved
                   strip at the top so the badge never lands on the store
                   block's QR code (which also sits top-right). With copy
                   captions off there is no badge, so the strip is dropped and
                   the ticket gets the full height of the sheet. */
                position: relative;
                padding-top: ${printTwoCopies ? 16 : 0}px;
                box-sizing: border-box;
              }
              @page {
                size: ${paperSize} ${orientation};
                margin: ${margin}mm;
              }
              /* Copy caption, pinned to the TOP-RIGHT CORNER of the sheet so
                 both the customer and the shop can tell at a glance which copy
                 they are holding. Absolutely positioned (not a block above the
                 ticket) so it does not push the layout down or alter pagination. */
              .copy-label {
                position: absolute;
                top: 0;
                right: 0;
                z-index: 5;
                font-size: 9px;
                font-weight: 700;
                letter-spacing: 1.5px;
                text-transform: uppercase;
                color: #334155;
                border: 1px solid #94A3B8;
                border-radius: 3px;
                padding: 2px 6px;
                background: #ffffff;
                white-space: nowrap;
              }
              /* Hard page break AFTER every page but the last, so each page gets
                 its own side of a sheet and the job does not end with a blank
                 one. */
              .print-ticket + .print-ticket {
                break-before: page;
                page-break-before: always;
              }
              /* On-screen reminder in the print window only — the browser's print
                 dialog is the only place duplex can be switched on, and CSS has
                 no way to request it. Hidden on paper. */
              .duplex-note {
                max-width: ${copyWidthMm}mm;
                margin: 0 auto 6mm;
                padding: 8px 12px;
                border: 1px solid #F59E0B;
                border-radius: 4px;
                background: #FFFBEB;
                color: #92400E;
                font-family: 'Roboto', 'Inter', sans-serif;
                font-size: 12px;
                line-height: 1.4;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                }
                .duplex-note {
                  display: none;
                }
              }
            </style>
          </head>
          <body>
            ${
              anyBack
                ? `<div class="duplex-note">
                     <b>Two-sided ticket.</b> Turn on <b>Print on both sides</b>
                     (duplex) in the print dialog so the reverse lands on the back
                     of the same sheet. ${pages.length} pages =
                     ${PRINT_COPIES.length} sheet${
                    PRINT_COPIES.length > 1 ? "s" : ""
                  }.
                   </div>`
                : ""
            }
            ${pages
              .map(
                (page) => `
              <div class="print-ticket">
                ${
                  page.label
                    ? `<div class="copy-label">${page.label}</div>`
                    : ""
                }
                ${htmlForCopy(page.html, page.label)}
              </div>`,
              )
              .join("")}
            <script>
              // Fit each copy onto its own sheet. The copy box is already sized
              // to the printable area, so measuring it here matches what the
              // printer will do — a template that overshoots by a few mm gets
              // scaled down instead of spilling a near-blank extra page after
              // every copy. Floor of 0.7 so it can never shrink to unreadable.
              const fitCopiesToSheet = () => {
                document.querySelectorAll(".print-ticket").forEach((copy) => {
                  const body = copy.querySelector(".pawn-ticket-preview");
                  if (!body) return;
                  const reserved =
                    parseFloat(getComputedStyle(copy).paddingTop) || 0;
                  const avail = copy.clientHeight - reserved;
                  const needed = body.scrollHeight;
                  if (needed > avail && avail > 0) {
                    body.style.zoom = String(Math.max(0.7, avail / needed));
                  }
                });
              };
              window.onload = () => {
                fitCopiesToSheet();
                window.print();
                window.onafterprint = () => window.close();
              };
            <\/script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
    document.title = originalTitle;
  };

  // Preview dimensions
  // One ticket face (already oriented; half a sheet when printing side by side).
  // The flow sections are built for a full page; squeezed into a half sheet
  // they wrap into an unreadable column. So on a half-sheet ticket the flow
  // layout is laid out wider (ticket ÷ flowHalfScale) and scaled down to the
  // half — the preview and the printed page alike. The canvas needs none of
  // this: it is designed at the real size.
  const flowHalfScale = twoUp ? 0.65 : 1;
  const previewWidth = `${ticketDimsMm[0] / flowHalfScale}mm`;
  const previewMinHeight = `${ticketDimsMm[1] / flowHalfScale}mm`;

  // Fit the fixed physical-size preview into whatever width the panel has.
  // The paper is a fixed cm width (A4/A5); on phones and tablet portrait that
  // is wider than the viewport, so we scale it down to fit. `zoom` reflows the
  // layout box (unlike transform: scale), so no empty space or clipping and the
  // print output — which uses a separate window — is unaffected.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Below md the builder panel stops sitting beside the ticket and stacks above
  // it, so every expanded settings group pushes the preview off screen. The cards
  // therefore start collapsed there and expanded on a wide layout, where there is
  // a column to spare.
  const isStackedPanel = useMediaQuery(theme.breakpoints.down("md"));
  const cardsOpenByDefault = !isStackedPanel;
  const previewPaperRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  useEffect(() => {
    const el = previewPaperRef.current;
    if (!el) return;
    const CM_TO_PX = 37.7952755906;
    const compute = () => {
      // Side by side: the preview shows the WHOLE sheet (both copies).
      const paperCmWidth = (twoUp ? sheetDimsMm[0] + 2 : ticketDimsMm[0]) / 10;
      const paperPx = paperCmWidth * CM_TO_PX;
      const styles = window.getComputedStyle(el);
      const padX =
        (parseFloat(styles.paddingLeft) || 0) +
        (parseFloat(styles.paddingRight) || 0);
      const avail = el.clientWidth - padX;
      const next = avail > 0 ? Math.min(1, avail / paperPx) : 1;
      setPreviewScale(Number.isFinite(next) && next > 0 ? next : 1);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [paperSize, orientation, layoutMode, twoUp]);

  // Watermark style
  const watermarkStyle = showWatermark
    ? {
        position: "relative",
        "&::after": {
          content: `"${watermarkText}"`,
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%) rotate(-45deg)",
          fontSize: "3rem",
          color: "rgba(0,0,0,0.05)",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          zIndex: 1,
        },
      }
    : {};

  // Build the QR code value: a URL pointing to the public, read-only ticket
  // viewer with the ticket data packed into the ?d= query param. Scanning it on
  // a phone opens a formatted card (see PawnTicketView). The data rides inside
  // the URL, so no backend lookup is needed.
  const buildQrPayload = () => {
    const payload = buildTicketPayload({
      store: {
        name: storeDetails.name,
        address: storeDetails.address,
        phone: storeDetails.phone,
        email: storeDetails.email,
        pblId: storeDetails.pblId,
        // store logo intentionally omitted — see ticketQrCodec buildTicketPayload
      },
      ticket: {
        voucherNo: ticketDetails.voucherNo,
        date: ticketDetails.date,
        time: ticketDetails.time,
      },
      customer: {
        name: customerDetails.name,
        mobile: customerDetails.mobile,
        address: customerDetails.address,
      },
      loan: {
        principal: summary.totalLoanAmount || loanDetails.principal,
        itemValue: summary.totalOrnamentValue || loanDetails.itemValue,
        interestRate: loanDetails.interestRate,
        interestAmount:
          summary.totalInterestAmount || loanDetails.interestAmount,
        paidAmount: summary.totalPaymentAmount || paymentDetails.paymentAmount,
        scheme: loanDetails.scheme,
      },
      ornaments: items.map((it) => ({
        name: it.description,
        grossWeight: it.grossWeight,
        value: it.value,
      })),
      isReceipt,
    });

    // Base URL the QR points to. Prefer the configured public URL
    // (REACT_APP_TICKET_VIEWER_URL, e.g. https://www.dailytick.com) so the link
    // is reachable from a customer's phone regardless of where the ticket was
    // generated. Falls back to the current origin when unset.
    const configuredBase = process.env.REACT_APP_TICKET_VIEWER_URL;
    const origin =
      typeof window !== "undefined" && window.location
        ? window.location.origin
        : "";
    const base = (configuredBase || origin || "").replace(/\/+$/, "");
    return `${base}/pawn/ticket-view?d=${encodeTicketData(payload)}`;
  };

  // `inCanvas`: rendered inside a free-form box, which sets its own spacing.
  // `list`: the section list of the copy being drawn (copies can differ).
  const renderSection = (sectionId, inCanvas = false, list = sections) => {
    const totalWeight = items.reduce((sum, i) => sum + (i.weight || 0), 0);
    const totalGrossWeight = items.reduce(
      (sum, i) => sum + (i.grossWeight || 0),
      0,
    );
    const principalWords = numberToWords(loanDetails.principal);
    const tableStyles = getTableStyles();
    // The "Ticket Number & Date" section is folded into the store block, so its
    // toggle controls the ticket-meta sub-block (voucher/amount/date/time) here.
    // Absent from the list (some layouts) → treated as visible.
    const ticketNoVisible =
      list.find((s) => s.id === "ticketNo")?.visible !== false;

    const sectionStyle = {
      mb: inCanvas ? 0 : 2,
      border: showBorders
        ? `${borderWidth}px ${borderStyle} ${borderColor}`
        : "none",
      borderRadius: showBorders ? 1 : 0,
      p: showBorders ? 1.5 : 0,
    };

    const headerStyle = {
      fontWeight: "bold",
      borderBottom: showBorders
        ? `2px solid ${primaryColor}`
        : "1px solid #ccc",
      mb: 1.5,
      pb: 0.5,
      color: primaryColor,
      textAlign: headerAlignment,
    };

    switch (sectionId) {
      // Heading / invocation line (e.g. "॥ ॐ नमः शिवाय ॥"). A draggable section
      // so stores can keep it at the very top or move it to the end as a closing
      // footer note. Content still comes from the "Show Top Heading" toggle and
      // the "Heading / Invocation" text in Extras.
      case "header":
        if (!showHeaderMessage || !headerText.trim()) return null;
        return (
          <Typography
            sx={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: "1.05em",
              letterSpacing: 0.3,
              color: "#1a1a1a",
              whiteSpace: "pre-wrap",
            }}
          >
            {headerText}
          </Typography>
        );

      case "store":
        return (
          <Box sx={sectionStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              {/* LEFT — store image + details at top, then a divider, then the
                  ticket meta (voucher no, loan amount, date & time) below. This
                  absorbs what used to be the standalone "Ticket Number & Date"
                  section, saving vertical space so tickets fit on one page. */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Store image + details */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {storeDetails.image && (
                    <Box
                      component="img"
                      src={storeDetails.image}
                      alt={storeDetails.name || "Store"}
                      crossOrigin="anonymous"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      sx={{
                        maxHeight: 90,
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", color: primaryColor }}
                    >
                      {storeDetails.name || "Pawn Shop"}
                    </Typography>
                    {storeDetails.address && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#555",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <LocationOnRoundedIcon
                          sx={{ fontSize: 18, color: "primary.main" }}
                        />
                        {storeDetails.address}
                      </Typography>
                    )}
                    {storeDetails.phone && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#555",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <PhoneRoundedIcon
                          sx={{ fontSize: 18, color: "primary.main" }}
                        />
                        {storeDetails.phone}
                      </Typography>
                    )}
                    {storeDetails.email && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#555",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <EmailRoundedIcon
                          sx={{ fontSize: 18, color: "primary.main" }}
                        />
                        {storeDetails.email}
                      </Typography>
                    )}
                    {storeDetails.pblId && (
                      <Typography variant="body2" sx={{ color: "#555" }}>
                        P.B.L : {storeDetails.pblId}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Ticket meta — voucher no, loan amount, date & time. Gated by
                    the "Ticket Number & Date" section toggle. */}
                {ticketNoVisible && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: 0.5,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Voucher No:</strong>{" "}
                        {ticketDetails.voucherNo || "—"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Loan Amount:</strong> ₹
                        {(
                          summary.totalLoanAmount || loanDetails.principal
                        ).toLocaleString("en-IN")}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Date:</strong>{" "}
                        {ticketDetails.date
                          ? new Date(ticketDetails.date).toLocaleDateString(
                              "en-IN",
                            )
                          : "—"}
                      </Typography>
                      {ticketDetails.time && (
                        <Typography variant="body2">
                          <strong>Time:</strong> {ticketDetails.time}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Box>

              {/* RIGHT — Auto-generated QR code. Encodes a URL to the public
                  read-only ticket viewer; scanning it opens a formatted card.
                  Rendered as inline SVG so it survives the outerHTML clone used
                  by handlePrint. */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <QRCodeSVG
                  value={buildQrPayload()}
                  // Bigger physical size = bigger modules. Weak (Android)
                  // cameras that couldn't resolve the old 150px grid can lock
                  // onto a 220px one far more easily.
                  size={220}
                  // Level "L" (7% redundancy) uses the FEWEST modules for a
                  // given payload, so the grid stays as coarse as possible —
                  // the priority here is resolvability on cheap cameras, not
                  // damage tolerance. marginSize={4} is the spec-recommended
                  // quiet zone — too small a margin is a common cause of scans
                  // failing.
                  level="L"
                  marginSize={4}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#777", mt: 0.5, fontSize: 10 }}
                >
                  Scan for details
                </Typography>
              </Box>
            </Box>
          </Box>
        );

      case "ticketNo":
        return (
          <Box sx={sectionStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="body1">
                <strong>Voucher No:</strong> {ticketDetails.voucherNo || "—"}
              </Typography>
              <Typography variant="body1">
                <strong>Date:</strong>{" "}
                {ticketDetails.date
                  ? new Date(ticketDetails.date).toLocaleDateString("en-IN")
                  : "—"}
              </Typography>
              {ticketDetails.time && (
                <Typography variant="body1">
                  <strong>Time:</strong> {ticketDetails.time}
                </Typography>
              )}
            </Box>
          </Box>
        );

      case "customer":
        return (
          <Box sx={sectionStyle}>
            <Typography variant="subtitle1" sx={headerStyle}>
              Customer Details
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                flexWrap: "wrap",
              }}
            >
              {/* Details — stacked vertically on the left */}
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    <strong>Name:</strong> {customerDetails.name || "—"}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Mobile:</strong> {customerDetails.mobile || "—"}
                  </Typography>
                  {customerDetails.sonOf && (
                    <Typography variant="body2">
                      <strong>S/W/D of:</strong> {customerDetails.sonOf}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Address:</strong> {customerDetails.address || "—"}
                  </Typography>
                  {customerDetails.aadhar && (
                    <Typography variant="body2">
                      <strong>Aadhar:</strong> {customerDetails.aadhar}
                    </Typography>
                  )}
                  {customerDetails.pan && (
                    <Typography variant="body2">
                      <strong>PAN:</strong> {customerDetails.pan}
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* Photos on the right — show only whichever image is present */}
              {(customerImage || ornamentImage) && (
                <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
                  {customerImage && (
                    // tc-cust-photo: dropped from the Customer Copy at print
                    // time when hideCustomerPhotoOnCustomerCopy is on.
                    <Box className="tc-cust-photo" sx={{ textAlign: "center" }}>
                      <Box
                        component="img"
                        src={customerImage}
                        alt="Customer"
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: "cover",
                          borderRadius: "6px",
                          border: "1px solid #E2E8F0",
                          display: "block",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.25,
                          color: "#64748B",
                          fontWeight: 600,
                        }}
                      >
                        Customer
                      </Typography>
                    </Box>
                  )}
                  {ornamentImage && (
                    <Box sx={{ textAlign: "center" }}>
                      <Box
                        component="img"
                        src={ornamentImage}
                        alt="Ornaments"
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: "cover",
                          borderRadius: "6px",
                          border: "1px solid #E2E8F0",
                          display: "block",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.25,
                          color: "#64748B",
                          fontWeight: 600,
                        }}
                      >
                        Ornaments
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
        );

      case "loan":
        return (
          <Box sx={sectionStyle}>
            <Typography variant="subtitle1" sx={headerStyle}>
              Loan Details
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Principal Amount:</strong>{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 800, fontSize: "1.15em", color: INK }}
                  >
                    ₹{loanDetails.principal.toLocaleString("en-IN")}
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Interest Rate:</strong>{" "}
                  {annualRateText(loanDetails.interestRate)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: "#555" }}
                >
                  {principalWords}
                </Typography>
              </Grid>
              {loanDetails.scheme && (
                <Grid
                  item
                  xs={12}
                  justifySelf={isMobile ? "flex-start" : "flex-end"}
                >
                  <Typography variant="body2">
                    <strong>Scheme:</strong> {loanDetails.scheme}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        );

      case "payment":
        return (
          <Box sx={sectionStyle}>
            <Typography variant="subtitle1" sx={headerStyle}>
              Payment Details
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Payment Amount:</strong> ₹
                  {paymentDetails.paymentAmount.toLocaleString("en-IN")}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Payment Method:</strong>{" "}
                  {paymentDetails.paymentMethod || "—"}
                </Typography>
              </Grid>
              {paymentDetails.splitPayment && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Cash Amount:</strong> ₹
                      {paymentDetails.cashAmount.toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Bank Transfer:</strong> ₹
                      {paymentDetails.bankAmount.toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        );

      case "items":
        return (
          <Box sx={sectionStyle}>
            <Typography variant="subtitle1" sx={headerStyle}>
              Items Pawned
            </Typography>
            {items.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ py: 2, textAlign: "center" }}
              >
                No items added
              </Typography>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mb: 1 }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: tableStyles.headerBg }}>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Item Description</TableCell>
                        <TableCell align="center">Pcs</TableCell>
                        <TableCell align="right">Gross Wt (g)</TableCell>
                        <TableCell align="right">Net Wt (g)</TableCell>
                        <TableCell align="right">Value (₹)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, idx) => (
                        <TableRow
                          key={item.id}
                          sx={{
                            bgcolor:
                              idx % 2 === 0
                                ? tableStyles.rowEvenBg
                                : tableStyles.rowOddBg,
                          }}
                        >
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>
                            {item.description}
                            {item.metal && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="textSecondary"
                              >
                                {item.metal}{" "}
                                {item.purity ? `(${item.purity}%)` : ""}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{item.pcs}</TableCell>
                          <TableCell align="right">
                            {(item.grossWeight || 0).toFixed(3)}
                          </TableCell>
                          <TableCell align="right">
                            {(item.weight || 0).toFixed(3)}
                          </TableCell>
                          <TableCell align="right">
                            {item.value.toLocaleString("en-IN")}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#fafafa" }}>
                        <TableCell colSpan={3} align="right">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{totalGrossWeight.toFixed(3)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>{totalWeight.toFixed(3)}</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            ₹
                            {summary.totalOrnamentValue.toLocaleString("en-IN")}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        );

      case "receiptSummary": {
        // Money received on a receipt/settlement ticket — replaces the ornaments
        // table. Shows interest paid, discount, insurance, charges, principal and
        // the grand total (matching the legacy receipt voucher).
        const ra = receiptAmounts || {};
        const total = Number(ra.totalPaid) || 0;
        const rows = [
          { label: "Interest Paid", value: ra.interestPaid, show: true },
          {
            label: "Interest Discount",
            value: ra.interestDiscount,
            show: (Number(ra.interestDiscount) || 0) > 0,
            negative: true,
          },
          {
            label: "Insurance Paid",
            value: ra.insurancePaid,
            show: (Number(ra.insurancePaid) || 0) > 0,
          },
          {
            label: "Other Charges",
            value: ra.otherCharges,
            show: (Number(ra.otherCharges) || 0) > 0,
          },
          {
            label: "Principal Paid",
            value: ra.principalPaid,
            show: (Number(ra.principalPaid) || 0) > 0,
          },
        ].filter((r) => r.show);
        return (
          <Box sx={sectionStyle}>
            <Typography variant="subtitle1" sx={headerStyle}>
              {isSettlement ? "Settlement Details" : "Receipt Details"}
            </Typography>
            {ra.loanVoucherNo && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Against Loan Ref:</strong> {ra.loanVoucherNo}
              </Typography>
            )}
            {ra.paymentMethod && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Payment Mode:</strong> {ra.paymentMethod}
              </Typography>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
              <Table size="small">
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.label}>
                      <TableCell>{r.label}</TableCell>
                      <TableCell align="right">
                        {r.negative ? "− " : ""}₹
                        {(Number(r.value) || 0).toLocaleString("en-IN")}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: "#fafafa" }}>
                    <TableCell>
                      <strong>Total Amount Paid</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>₹{total.toLocaleString("en-IN")}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography
              variant="body2"
              sx={{ fontStyle: "italic", color: "#555", mb: 0.5 }}
            >
              {numberToWords(Math.round(total))}
            </Typography>
            {ra.balanceAfter != null && (
              <Typography variant="body2">
                <strong>Balance Principal:</strong> ₹
                {(Number(ra.balanceAfter) || 0).toLocaleString("en-IN")}
              </Typography>
            )}
            {ra.interestPaidTill && (
              <Typography variant="body2">
                <strong>Interest Paid Till:</strong>{" "}
                {new Date(ra.interestPaidTill).toLocaleDateString("en-IN")}
              </Typography>
            )}
          </Box>
        );
      }

      case "signatures":
        return (
          <Box sx={sectionStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ textAlign: "center", minWidth: 150 }}>
                <Typography variant="body2">
                  {isReceipt ? "Signature of Customer" : "Signature of Pawnee"}
                </Typography>
                <Box
                  sx={{
                    borderTop: `1px dashed ${secondaryColor}`,
                    mt: 4,
                    pt: 0.5,
                    width: "100%",
                  }}
                />
              </Box>
              <Box sx={{ textAlign: "center", minWidth: 150 }}>
                <Typography variant="body2">
                  Signature of P.B or Agent
                </Typography>
                <Box
                  sx={{
                    borderTop: `1px dashed ${secondaryColor}`,
                    mt: 4,
                    pt: 0.5,
                    width: "100%",
                  }}
                />
              </Box>
            </Box>
          </Box>
        );

      case "terms":
        return (
          <Box sx={sectionStyle}>
            <Typography variant="subtitle1" sx={headerStyle}>
              Terms & Conditions
            </Typography>
            {/* Store-authored rich text — rendered exactly as typed (any
                language/script). ql-editor class inherits Quill's list/indent/
                align styling; font size follows the ticket's fontSizeScale. */}
            <Box
              className="ql-editor"
              sx={{
                p: 0,
                fontSize: "0.75rem",
                color: "#555",
                "& p, & ol, & ul": { m: 0 },
                "& ol, & ul": { pl: 2.5 },
              }}
              dangerouslySetInnerHTML={{
                __html: termsContent || DEFAULT_TERMS_HTML,
              }}
            />
          </Box>
        );

      // Closing note ("computer generated document…"). A section like any
      // other so it can be reordered or moved to the back of the sheet, but
      // deliberately unboxed — it reads as a caption under the ticket.
      case "footer":
        return (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="caption" color="textSecondary">
              {footerMessage}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  // ─── Free-form canvas wiring ───────────────────────────────────────────────
  const [canvasPaperW, canvasPaperH] = ticketDimsMm;

  // Ticket size changed under an existing layout → scale it to fit (one undo
  // step). A template load sets canvasDesignDims to its own size first, so
  // loading never triggers this.
  useEffect(() => {
    const from = canvasDesignDims.current;
    const to = [canvasPaperW, canvasPaperH];
    canvasDesignDims.current = to;
    if (!from) return;
    // Both copies — the one not on screen must fit the new size too.
    [canvasHistory, pawneeHistory].forEach((h) => {
      if (!h.elements.length) return;
      const next = rescaleElements(h.elements, from, to);
      if (next !== h.elements) h.set(next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasPaperW, canvasPaperH]);

  // Everything a data field can read (see FIELD_DEFS in canvasModel).
  const canvasCtx = {
    store: storeDetails,
    ticket: ticketDetails,
    customer: customerDetails,
    loan: loanDetails,
    payment: paymentDetails,
    summary,
    items,
    receipt: receiptAmounts || {},
    ticketTypeLabel: TICKET_TYPE_LABELS[activeTicketType],
    words: numberToWords,
  };
  const canvasImages = {
    logo: storeDetails.image || storeDetails.logo || "",
    customer: customerImage || "",
    ornament: ornamentImage || "",
  };
  const canvasQrValue = isCanvas ? buildQrPayload() : "";

  // A placed block renders the same section code the flow layout uses. The
  // ornaments table and receipt amounts swap with the ticket type exactly as in
  // flow mode, so one canvas template serves P, R and S tickets. The heading and
  // footer obey their Extras switches exactly as in flow mode: switched off,
  // the box stays on the canvas but prints nothing.
  const renderCanvasBlock = (section) => {
    const id =
      isReceipt && section === "items"
        ? "receiptSummary"
        : !isReceipt && section === "receiptSummary"
        ? "items"
        : section;
    if (id === "header" || id === "footer") {
      const on = id === "header" ? showHeaderMessage : showFooterMessage;
      const text = id === "header" ? headerText : footerMessage;
      return on && text?.trim() ? (
        <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>
      ) : null;
    }
    return (
      <Box
        sx={{
          "& .MuiTypography-root, & .MuiTableCell-root": {
            fontSize: "inherit",
          },
          "& .MuiTableCell-root": { py: 0.4, px: 0.75 },
        }}
      >
        {renderSection(id, true)}
      </Box>
    );
  };

  // What prints: hidden elements never do, and the copy caption only when the
  // template prints two captioned copies.
  const printElementsFor = (copy) =>
    elementsForCopy(copy).filter(
      (el) => !el.hidden && (printTwoCopies || el.type !== "copyLabel"),
    );

  const canvasSheetProps = {
    ctx: canvasCtx,
    qrValue: canvasQrValue,
    images: canvasImages,
    renderBlock: renderCanvasBlock,
    watermark: { show: showWatermark, text: watermarkText },
    fontScale: fontSizeScale,
  };

  const updateCanvasEls = (ids, updater, mergeKey) => {
    const set = new Set(ids);
    activeHistory.set(
      (list) => list.map((el) => (set.has(el.id) ? updater(el) : el)),
      mergeKey,
    );
  };

  // Add from the palette. Dropped elements are centred on the drop point; a
  // click drops it in the middle of the page. Either way it is kept on paper.
  const addCanvasElement = (type, opts = {}, at = {}) => {
    const maxZ = Math.max(0, ...canvasElements.map((e) => e.z || 0));
    const el = createElement(type, opts, { side: canvasSide, z: maxZ + 1 });
    const cx = at.x ?? canvasPaperW / 2;
    const cy = at.y ?? canvasPaperH / 2;
    el.w = Math.min(el.w, canvasPaperW);
    el.x = round1(Math.min(Math.max(0, cx - el.w / 2), canvasPaperW - el.w));
    el.y = round1(Math.min(Math.max(0, cy - el.h / 2), canvasPaperH - el.h));
    activeHistory.set((list) => [...list, el]);
    setCanvasSelection([el.id]);
  };

  const buildStarterCanvas = () =>
    normalizeElements(
      seedCanvasLayout({
        paperSize,
        orientation,
        paperW: canvasPaperW,
        paperH: canvasPaperH,
        margin,
        isReceipt,
        sections,
        showHeaderMessage: showHeaderMessage && !!headerText.trim(),
        showFooterMessage: showFooterMessage && !!footerMessage.trim(),
        printTwoCopies,
      }),
    );

  const switchLayoutMode = (mode) => {
    if (mode === layoutMode) return;
    if (mode === "canvas") {
      // First visit: both copies start from the same starter layout.
      const starter = canvasHistory.elements.length
        ? canvasHistory.elements
        : buildStarterCanvas();
      if (!canvasHistory.elements.length) canvasHistory.reset(starter);
      if (!pawneeHistory.elements.length) pawneeHistory.reset(starter);
    }
    setCanvasSelection([]);
    setCanvasSide("front");
    setLayoutMode(mode);
  };

  const rebuildCanvas = () => {
    setConfirmDialogConfig({
      title: "Start the free-form layout again?",
      message:
        "This replaces every element on the sheet with the starter layout",
      confirmText: "Replace layout",
      confirmColor: "warning",
      onConfirm: () => {
        // Only the copy being edited.
        activeHistory.set(buildStarterCanvas());
        setCanvasSelection([]);
        setConfirmDialogOpen(false);
      },
    });
    setConfirmDialogOpen(true);
  };

  // ─── Front / back placement ────────────────────────────────────────────────
  // Lay elements onto a face of the sheet. Each keeps its spot unless that spot
  // is taken there; then it docks at the bottom of the free space (signatures
  // belong there anyway), and a block it lands on — e.g. full-page terms — is
  // shortened to end just above it. Blocks shrink their content to fit, so
  // nothing is lost. A block arriving on an EMPTY face takes the whole page
  // inside the margins (terms on the reverse, the common case).
  const placeOnSide = (list, ids, side) => {
    const m = Math.min(margin, 8);
    const overlaps = (a, b) =>
      a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
    const next = list.map((el) => ({ ...el }));
    const isMoving = (el) => ids.includes(el.id);
    const faceEmpty = !next.some(
      (o) => o.side === side && !o.hidden && !isMoving(o),
    );
    let floor = canvasPaperH - m; // bottom of the free space, moving up
    next
      .filter(isMoving)
      .sort((p, q) => q.y + q.h - (p.y + p.h)) // lowest first keeps order
      .forEach((el, i) => {
        el.side = side;
        if (faceEmpty && i === 0 && ids.length === 1 && el.type === "block") {
          Object.assign(el, {
            x: m,
            y: m,
            w: round1(canvasPaperW - m * 2),
            h: round1(canvasPaperH - m * 2),
          });
          return;
        }
        const others = next.filter(
          (o) => o.side === side && o.id !== el.id && !o.hidden && !isMoving(o),
        );
        if (!others.some((o) => overlaps(el, o))) return;
        // A docked block gets room to be read: signature lines etc. squeezed
        // into a sliver would shrink to unreadable.
        if (el.type === "block") el.h = Math.max(el.h, 18);
        el.y = round1(Math.max(m, floor - el.h));
        floor = el.y - 1;
        others.forEach((o) => {
          if (!overlaps(el, o)) return;
          const h = round1(el.y - 2 - o.y);
          if (o.type === "block" && h >= 15) o.h = h;
        });
      });
    return next;
  };

  // Move elements to the other face and turn the editor to it, so they stay
  // selected and in view.
  const moveCanvasElsToSide = (ids, side) => {
    activeHistory.set((list) =>
      placeOnSide(
        list,
        ids.filter((id) => list.find((el) => el.id === id)?.side !== side),
        side,
      ),
    );
    setCanvasSide(side);
  };

  // Sections of the classic ticket with no box on this copy yet. The ornaments
  // table and receipt amounts are one slot (they swap with the ticket type), so
  // only the one that fits this ticket type is offered.
  const unplacedBlocks = BLOCK_DEFS.filter((b) => {
    if (b.id === (isReceipt ? "items" : "receiptSummary")) return false;
    const slot = (id) => (id === "receiptSummary" ? "items" : id);
    return !canvasElements.some(
      (el) => el.type === "block" && slot(el.props?.section) === slot(b.id),
    );
  });

  // Put a section that is not on the ticket yet onto a face (the "Not on the
  // ticket" group of the Front & Back list). Full width inside the margins.
  const addBlockToSide = (section, side) => {
    const m = Math.min(margin, 8);
    const maxZ = Math.max(0, ...canvasElements.map((e) => e.z || 0));
    const el = createElement("block", { section }, { side: "__new", z: maxZ + 1 });
    el.x = m;
    el.w = round1(canvasPaperW - m * 2);
    el.h = round1(Math.min(el.h, canvasPaperH - m * 2));
    el.y = m;
    activeHistory.set((list) => placeOnSide([...list, el], [el.id], side));
    setCanvasSide(side);
    setCanvasSelection([el.id]);
  };

  // ─── Copy linking / switching ──────────────────────────────────────────────
  const COPY_LABELS = { customer: "Customer Copy", pawnee: "Pawnee Copy" };

  const switchActiveCopy = (copy) => {
    if (copy === activeCopy) return;
    setActiveCopy(copy);
    setCanvasSelection([]);
  };

  // Untick "Same layout for both copies" → the Pawnee Copy gets its own layout,
  // starting as an exact copy of the Customer Copy's (unless it already has
  // one from an earlier split, which is restored as it was).
  const setCopiesLinked = (linked) => {
    if (linked) {
      setCopiesDiffer(false);
      setActiveCopy("customer");
      setCanvasSelection([]);
      return;
    }
    if (!pawneeSections) {
      setPawneeSections(customerSections.map((s) => ({ ...s })));
    }
    if (!pawneeHistory.elements.length && canvasHistory.elements.length) {
      pawneeHistory.reset(JSON.parse(JSON.stringify(canvasHistory.elements)));
    }
    setCopiesDiffer(true);
  };

  const canvasSelected = canvasSelection
    .map((id) => canvasElements.find((el) => el.id === id))
    .filter(Boolean);

  // Static, non-interactive copies of both faces at true size, kept off-screen.
  // Printing clones THESE (never the editor, which carries selection chrome),
  // and a real ticket's preview is the same component.
  const renderCanvasPrintSources = () => (
    <div aria-hidden style={OFFSCREEN_STYLE}>
      {COPY_KEYS.map((copy) => {
        const els = printElementsFor(copy);
        const back = els.filter((el) => el.side === "back");
        return (
          <React.Fragment key={copy}>
            <CanvasSheet
              {...canvasSheetProps}
              domId={`tc-print-${copy}-front`}
              elements={els.filter((el) => el.side !== "back")}
              paperW={canvasPaperW}
              paperH={canvasPaperH}
            />
            {back.length > 0 && (
              <CanvasSheet
                {...canvasSheetProps}
                domId={`tc-print-${copy}-back`}
                elements={back}
                paperW={canvasPaperW}
                paperH={canvasPaperH}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // Same for the flow layout: each copy's sheets at true size, off-screen, so
  // printing never depends on which copy the preview happens to show.
  const renderFlowPrintSources = () => (
    <div aria-hidden style={OFFSCREEN_STYLE}>
      {COPY_KEYS.map((copy) => {
        const list = sectionsForCopy(copy);
        const plan = copyPlans[copy];
        return (
          <React.Fragment key={copy}>
            {renderSheet(plan.frontSections, `tp-${copy}-front`, list, 1)}
            {plan.hasBackSide &&
              renderSheet(plan.backSections, `tp-${copy}-back`, list, 1)}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ─── What each face of the sheet renders ───────────────────────────────────
  // One ordered list of the sections that actually print, then split by side.
  // A function of the section list, because the two copies can differ.
  const sheetPlan = (list) => {
    const printableSections = list
      // "ticketNo" is now folded into the store section (left column), so skip it
      // as a standalone block to avoid an empty box/divider.
      .filter((s) => s.visible && s.id !== "ticketNo")
      // The heading/invocation and footer-note sections render nothing when their
      // Extras toggle is off or the text is empty — drop them so no orphan divider
      // is left behind wherever they sit in the order.
      .filter(
        (s) => s.id !== "header" || (showHeaderMessage && headerText.trim()),
      )
      .filter(
        (s) => s.id !== "footer" || (showFooterMessage && footerMessage.trim()),
      )
      // Ticket-type swap: a receipt/settlement ticket shows the money summary in
      // place of the ornaments table, and vice versa for a payment ticket. This
      // guarantees ornaments never appear on a receipt regardless of the loaded
      // template's section ids.
      // `srcId` remembers the section-list row this came from, so the Sections
      // card can still find its print position after the id was rewritten.
      .map((s) =>
        isReceipt && s.id === "items"
          ? { ...s, id: "receiptSummary", srcId: "items" }
          : !isReceipt && s.id === "receiptSummary"
          ? { ...s, id: "items", srcId: "receiptSummary" }
          : s,
      )
      // Drop any duplicate the swap may create (template with both ids).
      .filter((s, i, arr) => arr.findIndex((x) => x.id === s.id) === i);

    const frontCandidates = printableSections.filter(
      (s) => normalizeSide(s.side) !== "back",
    );
    const backCandidates = printableSections.filter(
      (s) => normalizeSide(s.side) === "back",
    );
    // Degenerate case — everything was pushed to the back. Print it one-sided
    // rather than emitting a blank front sheet for every copy.
    const [frontSections, backSections] = frontCandidates.length
      ? [frontCandidates, backCandidates]
      : [backCandidates, []];
    // Only then is the ticket two-sided: this drives the second preview sheet, the
    // extra print page per copy and the duplex reminder.
    return {
      frontCandidates,
      backCandidates,
      frontSections,
      backSections,
      hasBackSide: backSections.length > 0,
    };
  };
  // The copy being edited — drives the Sections card and the single preview.
  const {
    frontCandidates,
    backCandidates,
    frontSections,
    backSections,
    hasBackSide,
  } = sheetPlan(sections);
  const copyPlans = {
    customer: sheetPlan(sectionsForCopy("customer")),
    pawnee: sheetPlan(sectionsForCopy("pawnee")),
  };

  // The Sections card lists EVERY section — hidden ones included, since they are
  // toggled from there — grouped by the face it belongs to, so the top-to-bottom
  // order of each side is readable on its own.
  const sectionRowsBySide = {
    front: sections.filter((s) => normalizeSide(s.side) !== "back"),
    back: sections.filter((s) => normalizeSide(s.side) === "back"),
  };
  // Split the card into two groups as soon as anything is ASSIGNED to the back,
  // even if that block currently prints nothing — the user still has to see
  // where they put it. (hasBackSide, by contrast, is about real printed output.)
  const hasBackAssignment = sectionRowsBySide.back.length > 0;
  const backIsOnlySide = !frontCandidates.length && backCandidates.length > 0;

  // Each section's position in the printed stack of its own side. Keyed off the
  // sheets' own list, so a block that is switched off, whose Extras toggle is
  // off, or that is folded into another block gets NO number rather than a
  // misleading one.
  const printOrderById = {};
  [frontSections, backSections].forEach((side) => {
    side.forEach((s, i) => {
      printOrderById[s.id] = i + 1;
      if (s.srcId) printOrderById[s.srcId] = i + 1;
    });
  });

  // Why a row shows a dash instead of a print position.
  const notPrintingReason = (section) =>
    !section.visible
      ? "Switched off — not printed"
      : section.id === "ticketNo"
      ? "Printed inside the Store Details block, not as its own block"
      : section.id === "header"
      ? "Turn on Extras → Show Top Heading (and enter the text) to print this"
      : section.id === "footer"
      ? "Turn on Extras → Show Footer Message to print this"
      : "Not printed";

  // One face of the sheet. Both faces are identical paper boxes — same size,
  // padding, watermark and font scale — so the print clone of either one lands
  // on its page exactly like the single-sided ticket always has.
  // `list` = the copy's section list; `zoom` = on-screen scale (print ignores it).
  const renderSheet = (
    sheetSections,
    domId,
    list = sections,
    zoom = previewScale * flowHalfScale,
  ) => (
    <Box
      id={domId}
      className="pawn-ticket-preview"
      sx={{
        width: previewWidth,
        minHeight: previewMinHeight,
        bgcolor: "white",
        // Studio-only chrome: handlePrint's clone sets boxShadow inline to none,
        // so softening it here cannot reach the printed page.
        boxShadow:
          "0 2px 4px rgba(15,23,42,0.06), 0 12px 32px rgba(15,23,42,0.10)",
        borderRadius: 1,
        padding: `${margin / 2}mm`,
        transition: "all 0.2s ease",
        fontFamily: "Roboto, sans-serif",
        // The sheet is NOT a scroller. It grows to whatever the ticket needs and
        // the grey pane around it scrolls instead — an inner scrollbar here made
        // the printable page itself scroll, hiding the store header behind its
        // own scroll position.
        overflowY: "visible",
        flexShrink: 0,
        margin: "0 auto",
        zoom,
        fontSize: `${fontSizeScale}%`,
        "& .MuiTypography-root": { fontSize: "inherit" },
        "& .MuiTableCell-root": { fontSize: "inherit" },
        position: "relative",
        ...watermarkStyle,
      }}
    >
      {sheetSections.map((section, idx) => (
        <Box key={section.id}>
          {renderSection(section.id, false, list)}
          {section.id !== "signatures" &&
            idx !== sheetSections.length - 1 &&
            // The footer note brings its own top spacing and reads as a caption,
            // so no rule is drawn above it.
            sheetSections[idx + 1]?.id !== "footer" && (
              <Divider sx={{ my: 1.5 }} />
            )}
        </Box>
      ))}
    </Box>
  );

  // ─── Preview faces ─────────────────────────────────────────────────────────
  const activeCopyKey = editingPawnee ? "pawnee" : "customer";
  const previewHasBack = (copy) =>
    isCanvas
      ? printElementsFor(copy).some((el) => el.side === "back")
      : copyPlans[copy].hasBackSide;
  const anyPreviewBack = COPY_KEYS.some(previewHasBack);

  // One face of one copy, scaled for the pane. A copy with nothing on a face
  // shows a blank sheet (it prints blank too, keeping duplex pages paired).
  const renderPreviewFace = (copy, side, domId) => {
    if (isCanvas) {
      return (
        <CanvasSheet
          {...canvasSheetProps}
          copyLabel={COPY_LABELS[copy]}
          domId={domId}
          elements={printElementsFor(copy).filter((el) =>
            side === "back" ? el.side === "back" : el.side !== "back",
          )}
          paperW={canvasPaperW}
          paperH={canvasPaperH}
          scale={previewScale}
        />
      );
    }
    const plan = copyPlans[copy];
    return renderSheet(
      side === "back" ? plan.backSections : plan.frontSections,
      domId,
      sectionsForCopy(copy),
    );
  };

  // Both halves of a two-copies-per-sheet face. Customer Copy on the left.
  // With separate layouts, clicking a half makes it the copy being edited.
  const renderTwoUpRow = (side) => (
    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      {COPY_KEYS.map((copy, i) => {
        const active = copiesSplit && copy === activeCopyKey;
        return (
          <React.Fragment key={copy}>
            {i === 1 && (
              <Box
                sx={{
                  width: 0,
                  alignSelf: "stretch",
                  borderLeft: "1px dashed #94A3B8",
                  mx: 0.25,
                }}
              />
            )}
            <Box
              onClick={() =>
                isDesignerMode && copiesSplit && switchActiveCopy(copy)
              }
              title={
                isDesignerMode && copiesSplit && !active
                  ? `Click to edit the ${COPY_LABELS[copy]}`
                  : undefined
              }
              sx={{
                position: "relative",
                pt: 2.25,
                cursor:
                  isDesignerMode && copiesSplit && !active
                    ? "pointer"
                    : "default",
                opacity: copiesSplit && !active ? 0.75 : 1,
                "&:hover": { opacity: 1 },
                borderRadius: 1,
                outline: active ? `2px solid ${BLUE}` : "none",
                outlineOffset: 2,
              }}
            >
              <Typography
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 2,
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                  color: active ? BLUE : INK_MUTED,
                }}
              >
                {active ? `Editing · ${COPY_LABELS[copy]}` : COPY_LABELS[copy]}
              </Typography>
              {renderPreviewFace(copy, side)}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );

  // One draggable row of the Sections card. `ordinal` is the block's position in
  // the print order of ITS OWN side (null for a hidden section, which prints
  // nowhere), so the numbering answers "which sits on top of which" per face.
  const renderSectionRow = (section, ordinal) => {
    const isBack = normalizeSide(section.side) === "back";
    return (
      <SortableSection key={section.id} id={section.id}>
        {({ attributes, listeners }) => (
          <Paper
            elevation={0}
            sx={{
              px: 1,
              py: 0.6,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
              bgcolor: section.visible ? SURFACE : HEAD_BG,
              opacity: section.visible ? 1 : 0.75,
              transition: "background-color .15s ease, border-color .15s ease",
              "&:hover": { borderColor: BORDER_MED },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Position within this side's stack. A dash means the block does
                  not print at all — the tooltip says why. */}
              <Tooltip
                arrow
                title={ordinal ? "" : notPrintingReason(section)}
                disableHoverListener={!!ordinal}
              >
                <Box
                  sx={{
                    width: 19,
                    height: 19,
                    flexShrink: 0,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.63rem",
                    fontWeight: 700,
                    ...(ordinal
                      ? isBack
                        ? { bgcolor: BLUE, color: "#fff" }
                        : { bgcolor: BLUE_PALE, color: BLUE_DARK }
                      : {
                          border: `1px dashed ${BORDER_MED}`,
                          color: INK_MUTED,
                          cursor: "help",
                        }),
                  }}
                >
                  {ordinal || "–"}
                </Box>
              </Tooltip>
              {/* Drag handle — ONLY this grip is draggable, so the Switch on the
                  right stays clickable. */}
              <DragIndicatorRounded
                {...attributes}
                {...listeners}
                sx={{
                  fontSize: 19,
                  color: BORDER_MED,
                  cursor: "grab",
                  touchAction: "none",
                  "&:hover": { color: INK_MUTED },
                }}
              />
              <Typography
                sx={{ fontSize: "0.8rem", fontWeight: 600, color: INK_SOFT }}
              >
                {section.title}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {/* Which face of the sheet this block prints on. Greyed out while
                  the section is hidden — side is meaningless for something that
                  never prints. */}
              <Tooltip
                arrow
                title={
                  isBack
                    ? "Prints on the BACK of the sheet — tap to bring it to the front"
                    : "Prints on the FRONT — tap to move it to the back of the sheet"
                }
              >
                <Chip
                  size="small"
                  label={isBack ? "Back" : "Front"}
                  disabled={!section.visible}
                  onClick={() => toggleSectionSide(section.id)}
                  variant={isBack ? "filled" : "outlined"}
                  sx={{
                    height: 21,
                    minWidth: 54,
                    fontSize: "0.66rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    ...(isBack
                      ? {
                          bgcolor: BLUE,
                          color: "#fff",
                          "&:hover": { bgcolor: BLUE_DARK },
                        }
                      : { color: INK_MUTED, borderColor: BORDER_MED }),
                  }}
                />
              </Tooltip>
              <Switch
                checked={section.visible}
                onChange={() => toggleSection(section.id)}
                size="small"
              />
            </Box>
          </Paper>
        )}
      </SortableSection>
    );
  };

  // One face's list of sections, dragged independently of the other face. The
  // group heading only appears once something is on the back — a one-sided
  // template keeps the plain single list it always had.
  const renderSectionGroup = (side) => {
    const rows = sectionRowsBySide[side];
    if (!rows.length) return null;
    const isBack = side === "back";
    const shown = rows.filter((s) => printOrderById[s.id]).length;
    return (
      <Box sx={{ mt: isBack ? 2 : 0 }}>
        {hasBackAssignment && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography
              sx={{
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                color: isBack ? BLUE : INK_MUTED,
                flexShrink: 0,
              }}
            >
              {isBack
                ? backIsOnlySide
                  ? "Back of sheet — printing as the only side"
                  : "Back of sheet"
                : "Front of sheet"}
            </Typography>
            <Box sx={{ flexGrow: 1, height: "1px", bgcolor: BORDER }} />
            <Typography
              noWrap
              sx={{ fontSize: "0.6rem", color: INK_MUTED, flexShrink: 0 }}
            >
              {shown} printing, top to bottom
            </Typography>
          </Box>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSideDragEnd(side)}
        >
          <SortableContext
            items={rows.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={1}>
              {rows.map((section) =>
                renderSectionRow(section, printOrderById[section.id] || null),
              )}
            </Stack>
          </SortableContext>
        </DndContext>
      </Box>
    );
  };

  // Caption above each sheet in the preview, shown only for a two-sided ticket
  // so a one-sided template's preview is untouched.
  const sheetCaption = (text) => (
    <Typography
      sx={{
        fontSize: "0.6rem",
        fontWeight: 700,
        letterSpacing: "0.8px",
        textTransform: "uppercase",
        color: INK_MUTED,
        bgcolor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: "999px",
        px: 1.25,
        py: 0.25,
      }}
    >
      {text}
    </Typography>
  );

  // If no data and not in designer mode, show error
  if (!effectiveData && !isDesignerMode) {
    return (
      <Box
        sx={{
          p: { xs: 1.5, sm: 3 },
          bgcolor: "#f0f2f5",
          minHeight: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper sx={{ p: { xs: 2.5, sm: 5 }, textAlign: "center" }}>
          <Typography variant="h6" color="error">
            No ticket data found
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Please complete a payment first to generate a ticket, or use the
            Template Designer mode.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // Same rule as the report screens (see reportLayout.js): the PAGE never
        // scrolls. This is a flex column that fills the viewport and hands the
        // leftover height to the panels, which scroll internally — so the gap
        // above the ticket matches the gap below it instead of the page growing
        // past the panel and leaving dead space at the bottom.
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
        bgcolor: BG_ALT,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        // Kept scrollable rather than `hidden` so a very short window degrades
        // to a scroll instead of clipping the panels away.
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Grid
        container
        spacing={{ xs: 1.5, md: 2 }}
        sx={{ flex: 1, minHeight: 0, alignItems: "stretch" }}
      >
        {/* Left Panel - Controls (designer mode only).
            The items carry `height: 100%` rather than relying on align-items:
            stretch, because a Grid container wraps and a wrapping flex line's
            cross-size grows to its tallest item — left to itself the item sizes
            to the panel's content and overflows the page. */}
        {isDesignerMode && (
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              display: "flex",
              minHeight: 0,
              // min-width: auto (the flex default) would let the fixed-cm ticket
              // sheet widen this item and drag the page sideways.
              minWidth: 0,
              height: { md: "100%" },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.25, sm: 1.75 },
                borderRadius: 3,
                bgcolor: SURFACE,
                border: `1px solid ${BORDER}`,
                // Fills the column on desktop (page stays put, panel scrolls);
                // natural height when the panels stack on a phone.
                flex: 1,
                minWidth: 0,
                height: { md: "100%" },
                maxHeight: { xs: "none", md: "100%" },
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                "-ms-overflow-style": "none",
              }}
            >
              {/* Heading & Ticket Type */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 1.5,
                  pb: 1.5,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <PawnNavButtons />
                <Box sx={iconTileSx}>
                  <Receipt sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "1rem", sm: "1.12rem" },
                      color: INK,
                      letterSpacing: "-0.02em",
                      lineHeight: 1.25,
                    }}
                  >
                    Pawn Ticket Builder
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ fontSize: "0.72rem", color: INK_MUTED }}
                  >
                    {storeDetails.name}
                  </Typography>
                </Box>
              </Box>

              {/* Ticket Type */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={microLabelSx}>Ticket Type</Typography>
                {isDesignerMode ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {[
                      { value: "P", label: "Payment" },
                      { value: "R", label: "Receipt" },
                      { value: "S", label: "Settlement" },
                    ].map((type) => (
                      <Chip
                        key={type.value}
                        label={type.label}
                        onClick={() => setActiveTicketType(type.value)}
                        size="small"
                        sx={pillSx(activeTicketType === type.value)}
                      />
                    ))}
                  </Box>
                ) : (
                  <Chip
                    label={`${TICKET_TYPE_LABELS[activeTicketType]} Ticket`}
                    size="small"
                    sx={pillSx(true)}
                  />
                )}
              </Box>

              {/* Which copy the Sections card / canvas editor is changing —
                  only once the copies have separate layouts. */}
              {copiesSplit && (
                <Box
                  sx={{
                    mb: 1.5,
                    p: 1,
                    borderRadius: 2,
                    bgcolor: BLUE_PALE,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <Typography sx={microLabelSx}>Editing</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {COPY_KEYS.map((copy) => (
                      <Chip
                        key={copy}
                        label={COPY_LABELS[copy]}
                        size="small"
                        onClick={() => switchActiveCopy(copy)}
                        sx={pillSx(activeCopyKey === copy)}
                      />
                    ))}
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.66rem", color: INK_MUTED, mt: 0.75 }}
                  >
                    Sections, order and free-form elements apply to this copy
                    only. Paper, colours and shop details are shared.
                  </Typography>
                </Box>
              )}

              {/* Layout mode — per template. Flow stacks the sections; Free-form
                  lets the client place and size every element on the paper. */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={microLabelSx}>Layout</Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <Chip
                    label="Sections (flow)"
                    size="small"
                    onClick={() => switchLayoutMode("flow")}
                    sx={pillSx(!isCanvas)}
                  />
                  <Chip
                    icon={<DashboardCustomizeRounded sx={{ fontSize: 15 }} />}
                    label="Free-form canvas"
                    size="small"
                    onClick={() => switchLayoutMode("canvas")}
                    sx={pillSx(isCanvas)}
                  />
                  {isCanvas && (
                    <Button
                      size="small"
                      startIcon={<RestartAlt sx={{ fontSize: 16 }} />}
                      onClick={rebuildCanvas}
                      sx={{
                        ml: "auto",
                        textTransform: "none",
                        fontSize: "0.72rem",
                        color: INK_MUTED,
                      }}
                    >
                      Start over
                    </Button>
                  )}
                </Box>
                {isCanvas && (
                  <Typography
                    sx={{ fontSize: "0.68rem", color: INK_MUTED, mt: 0.75 }}
                  >
                    Drag anything on the sheet to move it, pull its handles to
                    resize (Shift keeps the shape). Saved with the template.
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: { xs: 1.5, sm: 2 },
                  alignItems: "flex-start",
                  mb: 1.75,
                }}
              >
                {/* ================= Current Template ================= */}
                <Box>
                  <Typography sx={microLabelSx}>Current Template</Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Autocomplete
                      value={
                        savedTemplates.find(
                          (t) => t.id === currentTemplateId,
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) loadTemplate(newValue);
                      }}
                      options={savedTemplates}
                      getOptionLabel={(option) => option.template_name}
                      loading={loading}
                      size="small"
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search templates..."
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading && <CircularProgress size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box
                            sx={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Box>
                              <Typography variant="body2">
                                {option.template_name}
                              </Typography>

                              {option.template_description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {option.template_description}
                                </Typography>
                              )}
                            </Box>

                            {option.is_default ? (
                              <Star sx={{ color: "#2563EB", fontSize: 20 }} />
                            ) : (
                              <StarBorder sx={{ fontSize: 20 }} />
                            )}
                          </Box>
                        </li>
                      )}
                    />

                    <Tooltip
                      title={
                        currentTemplateId &&
                        !savedTemplates.find((t) => t.id === currentTemplateId)
                          ?.is_default
                          ? "Set as default template"
                          : "Current template is default"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const currentTemplate = savedTemplates.find(
                              (t) => t.id === currentTemplateId,
                            );

                            if (
                              currentTemplate &&
                              !currentTemplate.is_default
                            ) {
                              setAsDefaultTemplate(currentTemplate);
                            }
                          }}
                          disabled={
                            !currentTemplateId ||
                            savedTemplates.find(
                              (t) => t.id === currentTemplateId,
                            )?.is_default
                          }
                        >
                          {savedTemplates.find(
                            (t) => t.id === currentTemplateId,
                          )?.is_default ? (
                            <Star sx={{ color: BLUE, fontSize: 20 }} />
                          ) : (
                            <StarBorder
                              sx={{ color: INK_MUTED, fontSize: 20 }}
                            />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Save current layout as new template">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setTemplateDialogOpen(true);
                          setTemplateName("");
                          setTemplateDescription("");
                          setSetAsDefault(false);
                        }}
                        sx={{
                          color: BLUE,
                          border: `1px solid ${BORDER}`,
                          borderRadius: 2,
                          "&:hover": { borderColor: BLUE, bgcolor: BLUE_PALE },
                        }}
                      >
                        <AddRounded sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* ================= Quick Layouts ================= */}
                {!isCanvas && (
                  <Box>
                    <Typography sx={microLabelSx}>Quick Layouts</Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                      }}
                    >
                      {Object.entries(LAYOUT_TEMPLATES).map(
                        ([key, template]) => (
                          <Chip
                            key={key}
                            label={template.name}
                            onClick={() => applyLayoutTemplate(key)}
                            size="small"
                            sx={pillSx(layoutTemplate === key)}
                          />
                        ),
                      )}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* ─── Free-form canvas tools ─── */}
              {isCanvas && (
                <>
                  <SettingsCard
                    title={
                      canvasSelected.length > 1
                        ? `${canvasSelected.length} Elements Selected`
                        : "Selected Element"
                    }
                    icon={<TuneRounded />}
                    defaultOpen
                    summary={canvasSelected.length ? "" : "Nothing selected"}
                  >
                    <CanvasProperties
                      selected={canvasSelected}
                      onUpdate={updateCanvasEls}
                      onDelete={() => {
                        const ids = new Set(canvasSelection);
                        canvasHistory.set((list) =>
                          list.filter((el) => !ids.has(el.id)),
                        );
                        setCanvasSelection([]);
                      }}
                      focusTextTick={focusTextTick}
                      onMoveToSide={moveCanvasElsToSide}
                    />
                  </SettingsCard>
                  <SettingsCard
                    title="Add Elements"
                    icon={<AddBoxRounded />}
                    defaultOpen={cardsOpenByDefault}
                    summary="Click to add · drag onto the sheet"
                  >
                    <CanvasPalette
                      onAdd={(type, opts) => addCanvasElement(type, opts)}
                      isReceipt={isReceipt}
                    />
                  </SettingsCard>
                  <SettingsCard
                    title="Front & Back of Sheet"
                    icon={<LayersRounded />}
                    defaultOpen={cardsOpenByDefault}
                    summary={`${
                      canvasElements.filter((el) => el.side !== "back").length
                    } front · ${
                      canvasElements.filter((el) => el.side === "back").length
                    } back`}
                  >
                    <CanvasLayers
                      elements={canvasElements}
                      selectedIds={canvasSelection}
                      onSelect={setCanvasSelection}
                      onUpdate={updateCanvasEls}
                      onMoveToSide={moveCanvasElsToSide}
                      onShowSide={setCanvasSide}
                      unplaced={unplacedBlocks}
                      onAddBlock={addBlockToSide}
                    />
                  </SettingsCard>
                </>
              )}

              {/* Shop details printed on the ticket: store master data, or the
                  template's own text per field. */}
              <SettingsCard
                title="Shop Details on Ticket"
                icon={<StorefrontRounded />}
                defaultOpen={false}
                summary={(() => {
                  const n = STORE_OVERRIDE_FIELDS.filter(
                    (f) => storeOverrides[f.key]?.custom,
                  ).length;
                  return n ? `${n} custom` : "Using store data";
                })()}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.25 }}
                >
                  Each detail prints from the store's own record unless you
                  switch it to <b>Custom</b> and type what this ticket should
                  show. Custom and left blank = that line is not printed.
                </Typography>
                <Stack spacing={1.25}>
                  {STORE_OVERRIDE_FIELDS.map((f) => {
                    const o = storeOverrides[f.key] || {};
                    const setO = (next) =>
                      setStoreOverrides((prev) => ({
                        ...prev,
                        [f.key]: { ...(prev[f.key] || {}), ...next },
                      }));
                    const storeValue = storeDetailsBase[f.key] || "";
                    return (
                      <Box key={f.key}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            sx={{
                              flex: 1,
                              fontSize: "0.76rem",
                              fontWeight: 600,
                              color: INK,
                            }}
                          >
                            {f.label}
                          </Typography>
                          <Chip
                            label="Store data"
                            size="small"
                            onClick={() => setO({ custom: false })}
                            sx={pillSx(!o.custom)}
                          />
                          <Chip
                            label="Custom"
                            size="small"
                            onClick={() =>
                              setO({
                                custom: true,
                                // Start from the store's value so a small
                                // edit doesn't mean retyping everything.
                                value:
                                  o.value !== undefined ? o.value : storeValue,
                              })
                            }
                            sx={pillSx(!!o.custom)}
                          />
                        </Box>
                        {o.custom ? (
                          <TextField
                            size="small"
                            fullWidth
                            multiline={!!f.multiline}
                            minRows={f.multiline ? 2 : undefined}
                            value={o.value || ""}
                            onChange={(e) => setO({ value: e.target.value })}
                            placeholder={`Leave blank to hide the ${f.label.toLowerCase()}`}
                            inputProps={
                              f.key === "phone"
                                ? { inputMode: "tel" }
                                : undefined
                            }
                            sx={fieldSx}
                          />
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "0.74rem",
                              color: storeValue ? INK_MUTED : BORDER_MED,
                              whiteSpace: "pre-wrap",
                              px: 0.25,
                            }}
                          >
                            {storeValue || "Not set in the store record"}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </SettingsCard>

              {/* Paper Settings */}
              <SettingsCard
                title="Paper Settings"
                icon={<ViewCompact />}
                defaultOpen={cardsOpenByDefault}
                summary={`${paperSize} ${
                  twoUp ? "landscape" : orientation
                } · ${margin}mm · ${
                  twoUp
                    ? "2 copies, 1 sheet"
                    : printTwoCopies
                    ? "2 copies"
                    : "1 copy"
                }`}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel>Paper Size</InputLabel>
                      <Select
                        value={paperSize}
                        label="Paper Size"
                        onChange={(e) => setPaperSize(e.target.value)}
                      >
                        <MenuItem value="A4">A4 (210x297mm)</MenuItem>
                        <MenuItem value="A5">A5 (148x210mm)</MenuItem>
                        <MenuItem value="Letter">Letter (8.5x11in)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={fieldSx}
                      // Side-by-side copies always use the sheet landscape.
                      disabled={twoUp}
                    >
                      <InputLabel>Orientation</InputLabel>
                      <Select
                        value={twoUp ? "landscape" : orientation}
                        label="Orientation"
                        onChange={(e) => setOrientation(e.target.value)}
                      >
                        <MenuItem value="portrait">Portrait</MenuItem>
                        <MenuItem value="landscape">Landscape</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={sliderLabelSx}>
                      Margin:{" "}
                      <Box
                        component="span"
                        sx={{ color: INK, fontWeight: 700 }}
                      >
                        {margin}mm
                      </Box>
                    </Typography>
                    <Slider
                      value={margin}
                      onChange={(e, val) => setMargin(val)}
                      min={5}
                      max={25}
                      step={1}
                      size="small"
                      sx={sliderSx}
                    />
                  </Grid>
                  {/* How many tickets come out of one transaction. Off means a
                      single, uncaptioned ticket. */}
                  <Grid item xs={12}>
                    <Divider sx={{ mb: 1, borderColor: BORDER }} />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={printTwoCopies}
                          onChange={(e) => setPrintTwoCopies(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Print 2 copies (Customer + Pawnee)"
                      sx={switchLabelSx}
                    />
                    <Typography
                      sx={{
                        display: "block",
                        mt: 0.25,
                        fontSize: "0.68rem",
                        color: INK_MUTED,
                      }}
                    >
                      {printTwoCopies
                        ? twoUp
                          ? "Both copies print on one sheet — “Customer Copy” on the left, “Pawnee Copy” on the right — with a dashed cut line between them."
                          : "Every payment, receipt or settlement prints two tickets, captioned “Customer Copy” and “Pawnee Copy”."
                        : "Every payment, receipt or settlement prints one ticket, with no copy caption on it."}
                    </Typography>
                    {printTwoCopies && (
                      <>
                        <FormControlLabel
                          sx={{ ...switchLabelSx, mt: 1 }}
                          control={
                            <Switch
                              checked={copiesSideBySide}
                              onChange={(e) =>
                                setCopiesSideBySide(e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Both copies on one sheet, side by side"
                        />
                        <FormControlLabel
                          sx={{ ...switchLabelSx, mt: 0.5, display: "flex" }}
                          control={
                            <Switch
                              checked={hideCustPhotoOnCustomerCopy}
                              onChange={(e) =>
                                setHideCustPhotoOnCustomerCopy(e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Customer Copy: ornaments photo only (Pawnee Copy keeps both)"
                        />
                        {/* Linked = one layout prints on both copies. Unticked,
                            each copy is designed on its own (Editing switch at
                            the top of this panel). */}
                        <FormControlLabel
                          sx={{ ...switchLabelSx, mt: 0.75, display: "flex" }}
                          control={
                            <Checkbox
                              size="small"
                              checked={!copiesDiffer}
                              onChange={(e) =>
                                setCopiesLinked(e.target.checked)
                              }
                              sx={{ py: 0.25 }}
                            />
                          }
                          label="Same layout & details on both copies"
                        />
                        <Typography
                          sx={{
                            display: "block",
                            fontSize: "0.68rem",
                            color: INK_MUTED,
                            pl: 3.5,
                          }}
                        >
                          {copiesDiffer
                            ? "The Customer Copy and the Pawnee Copy each have their own layout. Pick which one you are editing at the top of this panel" +
                              (twoUp
                                ? ", or click its half of the sheet."
                                : ".")
                            : "Untick to design the Customer Copy and the Pawnee Copy separately — e.g. show different details on each."}
                        </Typography>
                        {twoUp && (
                          <Typography
                            sx={{
                              display: "block",
                              mt: 0.25,
                              fontSize: "0.68rem",
                              color: INK_MUTED,
                            }}
                          >
                            {paperSize} turned landscape and cut in half — you
                            are designing one{" "}
                            <b>
                              {round1(ticketDimsMm[0])} ×{" "}
                              {round1(ticketDimsMm[1])}mm
                            </b>{" "}
                            ticket.
                            {isCanvas
                              ? " Use Start over (top) to rebuild the free-form layout at this size."
                              : " The sections are shrunk to fit the half sheet — switch off the ones you don't need, or use the Free-form canvas layout for a ticket designed at this size."}
                          </Typography>
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>
              </SettingsCard>

              {/* Typography Settings */}
              <SettingsCard
                title="Typography"
                icon={<FormatAlignCenter />}
                defaultOpen={cardsOpenByDefault}
                summary={`${fontSizeScale}% · ${headerAlignment} · ${tableStyle}`}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Typography sx={sliderLabelSx}>
                      Font Size:{" "}
                      <Box
                        component="span"
                        sx={{ color: INK, fontWeight: 700 }}
                      >
                        {fontSizeScale}%
                      </Box>
                    </Typography>
                    <Slider
                      value={fontSizeScale}
                      onChange={(e, val) => setFontSizeScale(val)}
                      min={70}
                      max={150}
                      step={5}
                      size="small"
                      sx={sliderSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel>Header Alignment</InputLabel>
                      <Select
                        value={headerAlignment}
                        label="Header Alignment"
                        onChange={(e) => setHeaderAlignment(e.target.value)}
                      >
                        <MenuItem value="left">Left</MenuItem>
                        <MenuItem value="center">Center</MenuItem>
                        <MenuItem value="right">Right</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <InputLabel>Table Style</InputLabel>
                      <Select
                        value={tableStyle}
                        label="Table Style"
                        onChange={(e) => setTableStyle(e.target.value)}
                      >
                        <MenuItem value="striped">Striped</MenuItem>
                        <MenuItem value="bordered">Bordered</MenuItem>
                        <MenuItem value="minimal">Minimal</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </SettingsCard>

              {/* Color Settings */}
              <SettingsCard
                title="Colors & Borders"
                icon={<Palette />}
                defaultOpen={cardsOpenByDefault}
                summary={
                  showBorders
                    ? `${primaryColor} · ${borderStyle} ${borderWidth}px`
                    : `${primaryColor} · no borders`
                }
              >
                <Grid container spacing={1.5} alignItems="flex-end">
                  <Grid item xs={6}>
                    <Typography sx={microLabelSx}>Primary Color</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="color"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      sx={colorFieldSx}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showBorders}
                          onChange={(e) => setShowBorders(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Show Borders"
                      sx={switchLabelSx}
                    />
                  </Grid>
                  {showBorders && (
                    <>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small" sx={fieldSx}>
                          <InputLabel>Border Style</InputLabel>
                          <Select
                            value={borderStyle}
                            label="Border Style"
                            onChange={(e) => setBorderStyle(e.target.value)}
                          >
                            <MenuItem value="solid">Solid</MenuItem>
                            <MenuItem value="dashed">Dashed</MenuItem>
                            <MenuItem value="dotted">Dotted</MenuItem>
                            <MenuItem value="double">Double</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={microLabelSx}>Border Color</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="color"
                          value={borderColor}
                          onChange={(e) => setBorderColor(e.target.value)}
                          sx={colorFieldSx}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={sliderLabelSx}>
                          Border Width:{" "}
                          <Box
                            component="span"
                            sx={{ color: INK, fontWeight: 700 }}
                          >
                            {borderWidth}px
                          </Box>
                        </Typography>
                        <Slider
                          value={borderWidth}
                          onChange={(e, val) => setBorderWidth(val)}
                          min={1}
                          max={5}
                          step={0.5}
                          size="small"
                          sx={sliderSx}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </SettingsCard>

              {/* Extras — watermark, footer, terms */}
              <SettingsCard
                title="Extras"
                icon={<Preview />}
                defaultOpen={cardsOpenByDefault}
                summary={
                  [
                    showHeaderMessage && "heading",
                    showWatermark && "watermark",
                    showFooterMessage && "footer",
                  ]
                    .filter(Boolean)
                    .join(" · ") || "none enabled"
                }
              >
                {/* Top heading / invocation line */}
                <FormControlLabel
                  sx={{ ...switchLabelSx, mb: 0.75 }}
                  control={
                    <Switch
                      checked={showHeaderMessage}
                      onChange={(e) => setShowHeaderMessage(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show Top Heading"
                />
                <TextField
                  disabled={!showHeaderMessage}
                  fullWidth
                  size="small"
                  label="Heading / Invocation"
                  placeholder="॥ ॐ नमः शिवाय ॥"
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value)}
                  helperText="Centered at the very top of the ticket. Any language."
                  sx={{ ...fieldSx, mb: 1.5 }}
                />
                {/* Watermark */}
                <FormControlLabel
                  sx={{ ...switchLabelSx, mb: 0.75 }}
                  control={
                    <Switch
                      checked={showWatermark}
                      onChange={(e) => setShowWatermark(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show Watermark"
                />
                <TextField
                  disabled={!showWatermark}
                  fullWidth
                  size="small"
                  label="Watermark Text"
                  value={watermarkText}
                  onChange={(e) => setWatermarkText(e.target.value)}
                  sx={{ ...fieldSx, mb: 1.5 }}
                />
                {/* Footer Message */}
                <FormControlLabel
                  sx={{ ...switchLabelSx, mb: 0.75 }}
                  control={
                    <Switch
                      checked={showFooterMessage}
                      onChange={(e) => setShowFooterMessage(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show Footer Message"
                />
                <TextField
                  disabled={!showFooterMessage}
                  fullWidth
                  size="small"
                  label="Footer Message"
                  value={footerMessage}
                  onChange={(e) => setFooterMessage(e.target.value)}
                  multiline
                  rows={2}
                  sx={{ ...fieldSx, mb: 1.5 }}
                />

                <Divider sx={{ my: 1.5 }} />

                {/* Rich-text Terms & Conditions — type/paste in ANY language and
                  it prints exactly as shown. Appears on the ticket only when the
                  "Terms & Conditions" section (below) is toggled on. */}
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                  Terms & Conditions
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Type or paste in any language — prints exactly as shown.
                  Enable the “Terms & Conditions” section below to place it on
                  the ticket, and set that section to <b>Back</b> to print the
                  terms on the reverse of the sheet.
                </Typography>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    "& .ql-toolbar": { borderColor: "divider" },
                    "& .ql-container": {
                      borderColor: "divider",
                      fontSize: "0.85rem",
                    },
                    "& .ql-editor": { minHeight: 120 },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={termsContent}
                    onChange={setTermsContent}
                    modules={TERMS_QUILL_MODULES}
                    formats={TERMS_QUILL_FORMATS}
                    placeholder="Enter terms & conditions…"
                  />
                </Box>
              </SettingsCard>

              {/* Sections Toggle & Drag Drop */}
              {!isCanvas && (
                <SettingsCard
                  title="Sections"
                  icon={<ViewList />}
                  defaultOpen={cardsOpenByDefault}
                  summary={
                    hasBackSide
                      ? `${frontSections.length} front · ${backSections.length} back`
                      : `${frontSections.length} printing`
                  }
                  action={
                    <Typography variant="caption" color="text.secondary">
                      Toggle &amp; drag to reorder
                    </Typography>
                  }
                >
                  {/* Front/back placement. Any section can go on the reverse of the
                    sheet — usually the Terms & Conditions, so the front keeps to
                    one page. */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1.25 }}
                  >
                    Tap the <b>Front</b> / <b>Back</b> chip to choose which side
                    of the sheet a section prints on.
                  </Typography>
                  {hasBackSide && (
                    <Alert
                      severity="info"
                      icon={<Layers fontSize="small" />}
                      sx={{
                        mb: 1.5,
                        py: 0,
                        "& .MuiAlert-message": { py: 0.75 },
                      }}
                    >
                      <Typography variant="caption">
                        {backSections.length} section
                        {backSections.length > 1 ? "s" : ""} on the back — each
                        copy prints 2 pages. Switch on{" "}
                        <b>Print on both sides</b> (duplex) in the print dialog.
                      </Typography>
                    </Alert>
                  )}
                  {/* One list per face of the sheet. Each is dragged on its own, so
                    the stacking order of the front and of the back are read and
                    rearranged separately. */}
                  {renderSectionGroup("front")}
                  {renderSectionGroup("back")}
                </SettingsCard>
              )}

              {/* Action Buttons */}
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${BORDER}` }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SaveAlt />}
                  onClick={() => setTemplateDialogOpen(true)}
                  fullWidth
                  sx={outlinedBtnSx}
                >
                  Save as New
                </Button>
                {currentTemplateId && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloudUpload />}
                    onClick={updateCurrentTemplate}
                    fullWidth
                    sx={outlinedBtnSx}
                  >
                    Update
                  </Button>
                )}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAlt />}
                  onClick={resetToSystemDefault}
                  fullWidth
                  sx={{
                    ...outlinedBtnSx,
                    color: INK_MUTED,
                    "&:hover": { borderColor: INK_MUTED, bgcolor: HEAD_BG },
                  }}
                >
                  Reset to Default
                </Button>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  startIcon={<PrintRounded />}
                  onClick={handlePrint}
                  sx={primaryBtnSx}
                >
                  Print / PDF
                </Button>
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Right Panel - Live Preview (the whole screen for a real ticket) */}
        <Grid
          item
          xs={12}
          md={isDesignerMode ? 7 : 12}
          sx={{
            position: "relative",
            display: "flex",
            minHeight: 0,
            // See the left panel: without this the sheet's fixed cm width sets
            // the item's minimum and the preview escapes the page instead of
            // being scaled down to fit (previewScale).
            minWidth: 0,
            height: { md: "100%" },
          }}
        >
          {/* Back / Dashboard for a real ticket, which has no header bar to
              hang them off. Mirrors the print control opposite: in the grey
              gutter at the sheet's top-left on sm+, pinned to the viewport
              below that, where the full-width sheet leaves no gutter. */}
          {!isDesignerMode && (
            <PawnNavButtons
              sx={{
                position: { xs: "fixed", sm: "absolute" },
                top: { sm: 12 },
                left: { xs: 16, sm: 12 },
                bottom: { xs: 24, sm: "auto" },
                zIndex: 5,
              }}
            />
          )}

          {/* ─── Print control for a saved transaction ─────────────────────
              A real ticket gets the whole page — no header bar, nothing to
              configure. The only control rides in the grey gutter beside the
              sheet's top-right corner, level with the paper. Below sm the
              sheet is scaled to the full width, leaving no gutter, so it
              becomes a floating button in the bottom-right instead. */}
          {!isDesignerMode && (
            <Tooltip
              title={
                printTwoCopies
                  ? "Print — Customer Copy + Pawnee Copy"
                  : "Print — single copy"
              }
              arrow
            >
              <IconButton
                onClick={handlePrint}
                sx={{
                  position: { xs: "fixed", sm: "absolute" },
                  top: { sm: 12 },
                  right: { xs: 16, sm: 12 },
                  bottom: { xs: 24, sm: "auto" },
                  zIndex: 5,
                  bgcolor: BLUE,
                  color: "#fff",
                  boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
                  "&:hover": { bgcolor: BLUE_DARK },
                }}
              >
                <PrintRounded />
              </IconButton>
            </Tooltip>
          )}

          {/* ─── Studio: print the sheet as previewed ──────────────────────
              The builder panel already carries "Print / PDF", but that is a
              scroll away once the settings cards are expanded. This is the
              same handler as a small badge pinned to the preview pane's
              top-right corner, so whatever the designer is looking at can be
              put on paper without leaving the sheet. It sits on the pane (not
              inside the scrolling Paper), so it stays put while the sheet is
              scrolled. */}
          {isDesignerMode && !isCanvas && (
            <Tooltip
              title={
                printTwoCopies
                  ? "Print this preview — Customer Copy + Pawnee Copy"
                  : "Print this preview"
              }
              arrow
            >
              <IconButton
                size="small"
                onClick={handlePrint}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  zIndex: 5,
                  bgcolor: BLUE,
                  color: "#fff",
                  boxShadow: "0 3px 10px rgba(37,99,235,0.35)",
                  "&:hover": { bgcolor: BLUE_DARK },
                }}
              >
                <PrintRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isCanvas && isDesignerMode ? (
            <TicketCanvasEditor
              history={activeHistory}
              companion={
                twoUp
                  ? {
                      // Customer Copy is the LEFT half of the sheet.
                      position: editingPawnee ? "left" : "right",
                      activeLabel:
                        COPY_LABELS[editingPawnee ? "pawnee" : "customer"],
                      label: COPY_LABELS[editingPawnee ? "customer" : "pawnee"],
                      activatable: copiesSplit,
                      onActivate: () => {
                        if (!copiesSplit) return;
                        switchActiveCopy(editingPawnee ? "customer" : "pawnee");
                      },
                      render: (scale) => (
                        <CanvasSheet
                          {...canvasSheetProps}
                          copyLabel={
                            COPY_LABELS[editingPawnee ? "customer" : "pawnee"]
                          }
                          elements={elementsForCopy(
                            editingPawnee ? "customer" : "pawnee",
                          ).filter(
                            (el) => el.side === canvasSide && !el.hidden,
                          )}
                          paperW={canvasPaperW}
                          paperH={canvasPaperH}
                          scale={scale}
                        />
                      ),
                    }
                  : null
              }
              side={canvasSide}
              onSideChange={(s) => {
                setCanvasSide(s);
                setCanvasSelection([]);
              }}
              showBackTab
              selectedIds={canvasSelection}
              onSelect={setCanvasSelection}
              paperW={canvasPaperW}
              paperH={canvasPaperH}
              safeMarginMm={Math.min(margin, 15)}
              sheetProps={{
                ...canvasSheetProps,
                copyLabel: COPY_LABELS[activeCopyKey],
              }}
              onAddElement={addCanvasElement}
              onRequestEdit={(id) => {
                setCanvasSelection([id]);
                setFocusTextTick((t) => t + 1);
              }}
            />
          ) : (
            <Paper
              ref={previewPaperRef}
              elevation={0}
              sx={{
                p: { xs: 1, sm: 2 },
                borderRadius: 3,
                bgcolor: CANVAS_BG,
                border: `1px solid ${BORDER}`,
                display: "flex",
                justifyContent: "center",
                // flex-start, not the default stretch: the sheet must keep its
                // paper proportions on a tall screen instead of being stretched
                // to the pane's height.
                alignItems: "flex-start",
                // THIS is the only scroller around the ticket. The sheet itself
                // never scrolls (see the preview Box below), so the page to be
                // printed stays whole and the user just scrolls down the pane to
                // read the bottom of it.
                overflow: "auto",
                flex: 1,
                minWidth: 0,
                height: { md: "100%" },
                maxHeight: { xs: "none", md: "100%" },
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                "-ms-overflow-style": "none",
              }}
            >
              {/* The sheet(s) to be printed. A one-sided ticket is a single
                sheet, exactly as before; once any section is moved to the back
                the reverse face appears below it, each labelled, so the studio
                shows both faces of the same physical sheet. */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                  margin: "0 auto",
                }}
              >
                {twoUp ? (
                  // Two copies per sheet: the whole landscape sheet, both
                  // halves, the cut line between — exactly as it prints.
                  <>
                    {anyPreviewBack && sheetCaption("Front of sheet")}
                    {renderTwoUpRow("front")}
                    {anyPreviewBack && (
                      <>
                        {sheetCaption("Back of sheet (reverse)")}
                        {renderTwoUpRow("back")}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {copiesSplit &&
                      sheetCaption(`Showing the ${COPY_LABELS[activeCopy]}`)}
                    {previewHasBack(activeCopyKey) &&
                      sheetCaption("Front of sheet")}
                    {renderPreviewFace(
                      activeCopyKey,
                      "front",
                      "pawn-ticket-preview",
                    )}
                    {previewHasBack(activeCopyKey) && (
                      <>
                        {sheetCaption("Back of sheet (reverse)")}
                        {renderPreviewFace(
                          activeCopyKey,
                          "back",
                          "pawn-ticket-preview-back",
                        )}
                      </>
                    )}
                  </>
                )}
              </Box>
            </Paper>
          )}
          {isCanvas ? renderCanvasPrintSources() : renderFlowPrintSources()}
        </Grid>
      </Grid>

      {/* Save Template Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.02rem", color: INK }}>
          Save Template
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={setAsDefault}
                onChange={(e) => setSetAsDefault(e.target.checked)}
              />
            }
            label="Set as default template"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={saveCurrentTemplate}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Templates Dialog */}
      <Dialog
        open={manageTemplatesOpen}
        onClose={() => setManageTemplatesOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1.02rem", color: INK }}>
          Manage Templates
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : savedTemplates.length === 0 ? (
            <Typography
              sx={{ py: 4, textAlign: "center" }}
              color="textSecondary"
            >
              No templates saved yet. Create your first template by customizing
              the layout and clicking "Save as New".
            </Typography>
          ) : (
            <List>
              {savedTemplates.map((template) => (
                <ListItem
                  key={template.id}
                  divider
                  secondaryAction={
                    <Box>
                      <Tooltip title="Load">
                        <IconButton
                          edge="end"
                          onClick={() => loadTemplate(template)}
                          sx={{ mr: 1 }}
                        >
                          <Download />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          template.is_default
                            ? "Default Template"
                            : "Set as Default"
                        }
                      >
                        <IconButton
                          edge="end"
                          onClick={() => setAsDefaultTemplate(template)}
                          sx={{ mr: 1 }}
                        >
                          {template.is_default ? (
                            <Star color="primary" />
                          ) : (
                            <StarBorder />
                          )}
                        </IconButton>
                      </Tooltip>
                      {!template.is_system && (
                        <Tooltip title="Delete">
                          <IconButton
                            edge="end"
                            onClick={() => deleteTemplate(template)}
                          >
                            <DeleteForever color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {template.template_name}
                        {template.is_system && (
                          <Chip
                            label="System"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {template.is_default && (
                          <Chip label="Default" size="small" color="primary" />
                        )}
                      </Box>
                    }
                    secondary={
                      template.template_description || "No description"
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageTemplatesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        templateName={templateToDelete?.template_name || ""}
      />

      {/* Reset Confirmation Dialog */}
      <ResetConfirmDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onConfirm={handleResetConfirm}
      />

      {/* Generic Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={
          confirmDialogConfig.onConfirm || (() => setConfirmDialogOpen(false))
        }
        title={confirmDialogConfig.title}
        message={confirmDialogConfig.message}
        confirmText={confirmDialogConfig.confirmText}
        confirmColor={confirmDialogConfig.confirmColor}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnTicketGenerator;
